import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiErr, coaDocUrl, coaUploadUrl, coaDocLegacyUrl } from "@/lib/api";
import { noAutomaticRateFor } from "@/lib/fxPanel";
import { PageHeader, Card, EmptyState, Btn, InfoTip } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Student as StudentIcon, Plus, PencilSimple, Trash, Key, GraduationCap, Buildings, Warning, Archive, ArrowCounterClockwise, Info, ArrowsClockwise, Paperclip, FilePdf, EnvelopeSimple, CaretDown, CaretRight, CheckCircle } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { beneficiaryFields, RECORD_TITLES } from "@/lib/recordFields";
import { CurrencySelect } from "@/components/CurrencySelect";
import { SchoolAutocomplete, SchoolEligibilityBadge, SchoolNotFoundNote } from "@/components/SchoolAutocomplete";
import { DeleteDialog } from "@/components/DeleteDialog";
import { useParties } from "@/lib/parties";
import { fmtDate, money, EXPENSE_GROUPS, localISO, HOUSING_SITUATION_LABEL, HOUSING_SITUATION_ORDER } from "@/lib/format";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { useDateErrors } from "@/lib/useDateErrors";
import { AotcCardControl, AotcReserveFields, useAotcReserve, offeredTaxYear } from "@/components/AotcReserveControl";
import { MoneyInput } from "@/lib/accountingFormat";
import { toast } from "sonner";
import { curCode } from "@/lib/currencies";
import { DOCUMENT_ACCEPT } from "@/lib/uploads";
import { AttachmentButton, AttachmentPanel, useAttachmentPanel } from "@/components/AttachmentView";
import { expandMonths, blockWindowStartYear, academicYearLabel, termMonthsShort } from "@/lib/schoolTerms";
import { NotesField } from "@/components/NotesField";

/** Map Federal School Code List country names → 2-letter codes the app uses. */
export const FSCL_COUNTRY_MAP = {
  "CANADA": "CA", "ENGLAND": "GB", "UNITED KINGDOM": "GB", "SCOTLAND": "GB", "WALES": "GB",
  "NORTHERN IRELAND": "GB", "MEXICO": "MX", "FRANCE": "FR", "GERMANY": "DE", "ITALY": "IT",
  "SPAIN": "ES", "SWITZERLAND": "CH", "AUSTRALIA": "AU", "JAPAN": "JP", "ISRAEL": "IL",
  "IRELAND": "IE", "NETHERLANDS": "NL", "AUSTRIA": "AT", "BELGIUM": "BE", "DENMARK": "DK",
  "SWEDEN": "SE", "NORWAY": "NO", "FINLAND": "FI", "NEW ZEALAND": "NZ", "SOUTH KOREA": "KR",
  "CHINA": "CN", "INDIA": "IN", "BRAZIL": "BR", "ARGENTINA": "AR", "CHILE": "CL",
  "COLOMBIA": "CO", "CZECH REPUBLIC": "CZ", "HUNGARY": "HU", "POLAND": "PL", "PORTUGAL": "PT",
  "GREECE": "GR", "TURKEY": "TR", "EGYPT": "EG", "SOUTH AFRICA": "ZA", "JAMAICA": "JM",
  "BARBADOS": "BB", "TRINIDAD AND TOBAGO": "TT", "COSTA RICA": "CR", "PANAMA": "PA",
  "DOMINICAN REPUBLIC": "DO", "PERU": "PE", "GRENADA": "GD", "ST. KITTS-NEVIS": "KN",
  "DOMINICA": "DM", "ST. LUCIA": "LC", "ST. VINCENT": "VC", "ANTIGUA-BARBUDA": "AG",
  "LEBANON": "LB", "JORDAN": "JO", "PHILIPPINES": "PH", "TAIWAN": "TW", "HONG KONG": "HK",
  "SINGAPORE": "SG", "MALAYSIA": "MY", "THAILAND": "TH",
};
export function normalizeCountryCode(fsclCountry) {
  if (!fsclCountry || fsclCountry === "US") return "US";
  const trimmed = fsclCountry.trim();
  const upper = trimmed.toUpperCase();
  return FSCL_COUNTRY_MAP[upper] || upper;
}

export default function Students() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dlg, setDlg] = useState(false); const [editing, setEditing] = useState(null);
  // The beneficiary behind the eye — read only. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null); // "end-date" | "add-school" | null
  const [loginFor, setLoginFor] = useState(null);
  const [archiveFor, setArchiveFor] = useState(null); const [deleteFor, setDeleteFor] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const { data: students = [], isLoading } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["students"] });

  // Auto-open edit dialog from URL params (e.g. /students?edit=<id>&focus=end-date)
  useEffect(() => {
    const editId = searchParams.get("edit");
    const focus = searchParams.get("focus");
    if (!editId || !students.length) return;
    const target = students.find((s) => s.id === editId);
    if (!target) return;
    setEditing(target);
    setFocusTarget(focus || null);
    setDlg(true);
    // Clear the params so a refresh does not reopen
    setSearchParams({}, { replace: true });
  }, [searchParams, students]); // eslint-disable-line

  // A link may name the tax year too (/students?aotc=<id>&year=2025); that
  // beneficiary's card then opens on it. Read once, before the address is cleared.
  const [aotcLink] = useState(() => ({ id: searchParams.get("aotc"), year: offeredTaxYear(searchParams.get("year")) }));
  // Deep-link: /students?aotc=<studentId> → scroll to that beneficiary's AOTC card control
  useEffect(() => {
    const aotcId = searchParams.get("aotc");
    if (!aotcId || !students.length) return;
    setSearchParams({}, { replace: true });
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="aotc-card-control-${aotcId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#034AA2]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#034AA2]", "ring-offset-2"), 4000);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchParams, students]); // eslint-disable-line

  const toggleBalVis = async (s, val) => {
    try { await api.put(`/students/${s.id}/balance-visibility`, { show_529_balance: val }); toast.success(`529 balance ${val ? "visible" : "hidden"} for ${s.full_name}`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setArchived = async (s, archived) => {
    try { await api.post(`/students/${s.id}/archive`, { archived }); toast.success(archived ? `${s.full_name} archived` : `${s.full_name} reactivated`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delStudent = async (s) => {
    try { await api.delete(`/students/${s.id}`); toast.success("Beneficiary deleted permanently"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const activeStudents = students.filter((s) => !s.archived);
  const archivedCount = students.length - activeStudents.length;
  const shown = showArchived ? students : activeStudents;

  return (
    <div>
      <PageHeader title={<span className="inline-flex items-center gap-2">Beneficiary(ies) <InfoTip testid="beneficiary-tooltip" size={18} width="w-80"><b>Beneficiary:</b> The individual a 529 account is meant to benefit — the student, apprentice, or professional whose qualified education expenses the funds pay. Always one named person, with no age or income limit. (IRS Publication 970)</InfoTip></span>} subtitle="Manage beneficiaries, school enrollments and logins."
        actions={<div className="flex flex-wrap items-center gap-2">
          {archivedCount > 0 && <Btn variant="ghost" className="h-9" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"><Archive size={14} weight="bold" /> {showArchived ? "Hide" : "Show"} archived ({archivedCount})</Btn>}
          <Btn onClick={() => { setEditing(null); setDlg(true); }} data-testid="add-student-btn"><Plus size={16} weight="bold" /> Add beneficiary</Btn>
        </div>} />

      {isLoading ? <div className="h-40 animate-pulse rounded-xl bg-white" /> : activeStudents.length === 0 && !showArchived ? (
        <EmptyState icon={StudentIcon} title="No beneficiaries yet" subtitle="Add your first beneficiary to start tracking." action={<Btn onClick={() => setDlg(true)}><Plus size={16} weight="bold" /> Add beneficiary</Btn>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {shown.map((s) => (
            <Card key={s.id} data-testid={`student-card-${s.id}`} className={s.archived ? "opacity-70" : ""}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#034AA2] text-white"><GraduationCap size={22} weight="fill" /></span>
                  <div className="min-w-0">
                    <div className="truncate text-lg font-extrabold">{s.full_name} {s.nickname && <span className="ml-1 rounded-full bg-[#F5D85B] px-2 py-0.5 text-xs font-bold text-[#0A0A0A]">{s.nickname}</span>}{s.archived && <span className="ml-1 rounded-full bg-[#6b7280] px-2 py-0.5 text-xs font-bold text-white">Archived</span>}</div>
                    <div className="truncate text-xs text-[#6b7280]">{s.login_email ? `Login: ${s.login_email}` : "No beneficiary login yet"}</div>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {/* The eye, immediately before the pencil. (ZEN-702) */}
                  <ViewButton onClick={() => setViewing(s)} testid={`view-student-${s.id}`} />
                  <button onClick={() => { setEditing(s); setDlg(true); }} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]" data-testid={`edit-student-${s.id}`}><PencilSimple size={16} weight="bold" /></button>
                  {s.archived
                    ? <button onClick={() => setArchived(s, false)} className="rounded p-1.5 text-[#077A39] hover:bg-[#077A39]/10" title="Reactivate" data-testid={`unarchive-student-${s.id}`}><ArrowCounterClockwise size={16} weight="bold" /></button>
                    : <button onClick={() => setArchiveFor(s)} className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB]" title="Archive" data-testid={`archive-student-${s.id}`}><Archive size={16} weight="bold" /></button>}
                  <button onClick={() => setDeleteFor(s)} className="rounded p-1.5 text-[#F01919] hover:bg-[#F01919]/10" title="Delete (entered in error)" data-testid={`del-student-${s.id}`}><Trash size={16} weight="bold" /></button>
                </div>
              </div>

              <div className="mt-3 space-y-1.5">
                {(s.schools || []).map((sc) => {
                  const grpLabel = EXPENSE_GROUPS.find(g => g.key === (sc.expense_group || "post_secondary"))?.label || "Higher Education";
                  return (
                  <div key={sc.id} className={`rounded-md bg-[#E9E9EB] px-3 py-1.5 text-xs ${sc.archived ? "opacity-60" : ""}`}>
                    <div className="flex items-center gap-2">
                      <Buildings size={14} weight="bold" className="text-[#077A39]" />
                      <span className="font-semibold">{sc.school_name}</span>
                      <span className="rounded-full bg-[#034AA2]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#034AA2]">{grpLabel}</span>
                      {sc.archived ? <span className="rounded-full bg-[#6b7280] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">archived</span> : <span className="rounded-full bg-[#077A39]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#077A39]">Active</span>}
                      <span className="text-[#6b7280]">{(sc.expense_group || "post_secondary") === "post_secondary" && sc.school_529_code ? ` · 529 code ${sc.school_529_code}` : ""} {sc.start_date ? `· from ${fmtDate(sc.start_date)}` : ""}{(sc.expense_group || "post_secondary") === "post_secondary" ? schoolCapSummary(sc) : ""}</span>
                    </div>
                    {(sc.expense_group || "post_secondary") === "post_secondary" && sc.school_529_code && <SchoolCodeBadge code={sc.school_529_code} />}
                  </div>
                  );
                })}
                {(!s.schools || s.schools.length === 0) && <div className="text-xs text-[#6b7280]">No education periods.</div>}
              </div>

              {/* AOTC control — Higher-Ed beneficiaries only */}
              {(s.schools || []).some(sc => !sc.archived && (sc.expense_group || "post_secondary") === "post_secondary") && (
                <AotcCardControl studentId={s.id} startYear={aotcLink.id === s.id ? aotcLink.year : null} />
              )}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[#C2C9D1] pt-3">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm font-semibold" title="When off, this beneficiary's login won't see the 529 account balance.">
                    <Switch checked={s.show_529_balance !== false} onCheckedChange={(v) => toggleBalVis(s, v)} data-testid={`bal-vis-${s.id}`} />
                    Show 529 balance
                  </label>
                </div>
                <div className="flex gap-2">
                  {/* An archived beneficiary is refused at sign-in and on every
                      request, so a login set on one can never work. Offering the
                      controls is a promise the application then breaks; the
                      server refuses them too. */}
                  {!s.archived && s.login_email && <ResendInviteBtn studentId={s.id} />}
                  {!s.archived && <Btn variant="green" className="h-8" onClick={() => setLoginFor(s)} data-testid={`login-${s.id}`}><Key size={14} weight="bold" /> {s.login_email ? "Reset login" : "Create login"}</Btn>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <StudentDialog open={dlg} onClose={() => { setDlg(false); setFocusTarget(null); }} student={editing} onSaved={refresh} focusTarget={focusTarget} />
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.beneficiary}
        fields={viewing ? beneficiaryFields(viewing) : []}
        testid="student-view"
      />
      {loginFor && <LoginDialog student={loginFor} onClose={() => setLoginFor(null)} onSaved={refresh} />}
      {archiveFor && <ArchiveDialog student={archiveFor} onClose={() => setArchiveFor(null)} onConfirm={() => { const done = setArchived(archiveFor, true); setArchiveFor(null); return done; }} />}

      {deleteFor && <DeleteStudentDialog student={deleteFor} onClose={() => setDeleteFor(null)} onArchive={() => { setArchiveFor(deleteFor); setDeleteFor(null); }} onDelete={() => { delStudent(deleteFor); setDeleteFor(null); }} />}
    </div>
  );
}

function ResendInviteBtn({ studentId }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${studentId}/resend-invite`);
      toast.success("Invitation email queued");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Btn variant="ghost" className="h-8" onClick={send} disabled={busy} data-testid={`resend-invite-${studentId}`}>
      <EnvelopeSimple size={14} weight="bold" /> {busy ? "Sending…" : "Resend invite"}
    </Btn>
  );
}

// Compact per-year cap summary for the student card (shows the entry with the latest period).
export function schoolCapSummary(sc) {
  const entries = (sc.coa_by_year || []).filter((y) => (y.periods || []).length);
  if (entries.length) {
    let best = entries[0], bestKey = "";
    for (const y of entries) {
      const k = (y.periods || []).reduce((mx, p) => (p.to_month > mx ? p.to_month : mx), "");
      if (k > bestKey) { bestKey = k; best = y; }
    }
    // ZEN-781 — ONE RULE for the summary: only terms with an amount entered in
    // the fields their mode uses count, a real $0 among them included; a
    // waiting term adds neither money nor months. A year with no such term but
    // a waiting one says "Not entered yet", never "$0".
    // A foreign-currency term with no exchange rate yet sets no dollar limit (S8).
    const hasRate = (p) => curCode(p.coa_currency, "USD") === "USD" || parseFloat(p.coa_fx_rate) > 0;
    const priced = (best.periods || []).filter((p) => !termWaitsForAmount(p) && hasActiveCoaAmount(p) && hasRate(p));
    if (!priced.length) return (best.periods || []).some(termWaitsForAmount) ? " · room & board cap: Not entered yet" : "";
    const totalMonths = priced.reduce((sum, p) => sum + (monthsBetween(p.from_month, p.to_month) || 1), 0);
    const annual = priced.reduce((sum, p) => {
      const m = monthsBetween(p.from_month, p.to_month) || 1;
      const fx = parseFloat(p.coa_fx_rate) || 1.0;
      const tot = (val) => { const v = parseFloat(val) || 0; const v_usd = v * fx; return p.basis === "monthly" ? v_usd * m : v_usd; };
      const used = (p.mode || "separate") === "combined" ? [p.combined] : [p.food, p.housing];
      return sum + used.reduce((t, v) => t + tot(v), 0);
    }, 0);
    const moLabel = totalMonths ? ` for ${totalMonths} month${totalMonths !== 1 ? "s" : ""}` : "";
    return ` · room & board cap ${money(annual, "USD")}${moLabel}${priced.length > 1 ? ` (${priced.length} periods)` : ""}`;
  }
  // legacy flat fields
  const ys = (sc.coa_by_year || []).filter((y) => y.food || y.housing || y.combined);
  if (!ys.length) return "";
  const y = [...ys].sort((a, b) => (b.year || 0) - (a.year || 0))[0];
  if ((y.mode || "separate") === "combined" && y.combined) return ` · ${y.year} room & board cap ${money(y.combined, "USD")}`;
  const parts = [];
  if (y.food) parts.push(`food ${money(y.food, "USD")}`);
  if (y.housing) parts.push(`housing ${money(y.housing, "USD")}`);
  return parts.length ? ` · ${y.year} cap ${parts.join(" + ")}` : "";
}

const currentAcademicYear = () => new Date().getFullYear();

/**
 * Resolves a field write against the field's current value. Passing a function
 * lets concurrent document uploads append to whatever has already landed
 * instead of to a snapshot taken when the upload started.
 */
export const resolveFieldUpdate = (update, current) => (typeof update === "function" ? update(current) : update);

/** Writes one field of one COA period, resolving `v` against that field's current value. */
export const applyPeriodField = (schools, i, yi, pi, k, v) => schools.map((s, idx) => idx === i
  ? { ...s, coa_by_year: s.coa_by_year.map((y, j) => j === yi
      ? { ...y, periods: y.periods.map((pp, k2) => k2 === pi ? (() => { const next = resolveFieldUpdate(v, pp[k]); return { ...pp, [k]: next, _autoDefault: false, _copied: keepCopied(pp, k, next) }; })() : pp) }
      : y) }
  : s);
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtMonthYear = (ym) => { if (!ym) return ""; const [y, m] = ym.split("-").map(Number); return `${MONTH_NAMES[(m || 1) - 1]} ${y}`; };
const fmtFullDate = (ds) => { if (!ds) return ""; const [y, m, d] = ds.split("-").map(Number); return `${MONTH_NAMES[(m || 1) - 1]} ${d}, ${y}`; };
const yearSpanLabel = (periods) => {
  if (!periods?.length) return "";
  const froms = periods.map((p) => p.from_month).filter(Boolean).sort();
  const tos = periods.map((p) => p.to_month).filter(Boolean).sort();
  if (!froms.length || !tos.length) return "";
  if (froms[0] === tos[tos.length - 1]) return fmtMonthYear(froms[0]);
  return `${fmtMonthYear(froms[0])} – ${fmtMonthYear(tos[tos.length - 1])}`;
};
// THE SAME SIX ARRANGEMENTS THE REST OF THE APP NAMES. (ZEN-635.) This list
// used to carry its own wording, the expense panel carried a second, and the
// downloaded tax document a third — so a family set "Off campus — with
// roommate(s)" here and read "Off campus roommates" on the next screen. The
// words now come from one table; this list only decides the ORDER they appear
// in, which is a decision about this form.
//
// The parenthetical hints that used to sit here went with the old wording. The
// two that were doing work are still on screen: the text box under "Other"
// already prompts for the school's category name, and the housing-type control
// on the row below says whether it is school housing.
const SITUATIONS = HOUSING_SITUATION_ORDER.map((k) => [k, HOUSING_SITUATION_LABEL[k]]);
const monthsBetween = (from, to) => {
  if (!from || !to) return 0;
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (!fy || !ty) return 0;
  const n = (ty - fy) * 12 + (tm - fm) + 1;
  return n > 0 ? n : 0;
};
const MONTH_INITIALS = ["J","F","M","A","M","J","J","A","S","O","N","D"];
const TERM_TINTS = ["#C8DBEF","#8FB4D9","#6B97C4","#4A7BA8"];
function _monthSeason(m, role = "start") {
  if (m <= 2) return "Winter";
  if (m <= 4) return "Spring";
  // August: "Fall" when starting a term, "Summer" when ending one
  if (m <= 7) return "Summer";
  if (m === 8) return role === "end" ? "Summer" : "Fall";
  if (m <= 10) return "Fall";
  return "Late fall";
}
function guessTermLabel(fromMonth, toMonth) {
  if (!fromMonth) return "";
  const [fy, fm] = fromMonth.split("-").map(Number);
  if (!fy || !fm) return "";
  const startSeason = _monthSeason(fm, "start");
  if (!toMonth) return `${startSeason} ${fy}`;
  const [, tm] = toMonth.split("-").map(Number);
  if (!tm) return `${startSeason} ${fy}`;
  const endSeason = _monthSeason(tm, "end");
  if (startSeason === endSeason) return `${startSeason} ${fy}`;
  // "Fall" and "Late fall" merge when adjacent
  if ((startSeason === "Fall" && endSeason === "Late fall") || (startSeason === "Late fall" && endSeason === "Fall"))
    return `Fall ${fy}`;
  return `${startSeason}\u2013${endSeason} ${fy}`;
}
function monthFullName(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_NAMES[(m || 1) - 1]} ${y}`;
}
/** Compute the academic-year window (Sep–Aug) containing the earliest covered month. */
function academicYearWindow(periods) {
  const allMonths = (periods || []).flatMap((p) => expandMonths(p.from_month, p.to_month));
  if (!allMonths.length) return [];
  const sorted = [...new Set(allMonths)].sort();
  const earliest = sorted[0];
  const latest = sorted[sorted.length - 1];
  const [ey, em] = earliest.split("-").map(Number);
  // Window starts Sep of (year - 1 if month < Sep, else year)
  const winStartY = em < 9 ? ey - 1 : ey;
  const winStart = `${winStartY}-09`;
  const winEndDefault = `${winStartY + 1}-08`;
  // Extend if any term goes beyond default window end
  const winEnd = latest > winEndDefault ? latest : winEndDefault;
  return expandMonths(winStart, winEnd);
}
/** Fixed Sep–Aug window for an academic year starting in September of `winStartY`. */
function fixedAcademicWindow(winStartY) {
  return expandMonths(`${winStartY}-09`, `${winStartY + 1}-08`);
}
function detectOverlaps(allPeriods) {
  const coverage = {};
  for (const p of allPeriods) {
    const ms = expandMonths(p.from_month, p.to_month);
    for (const m of ms) {
      if (!coverage[m]) coverage[m] = [];
      coverage[m].push(p);
    }
  }
  const overlaps = {};
  for (const [m, ps] of Object.entries(coverage)) {
    if (ps.length > 1) overlaps[m] = ps;
  }
  return overlaps;
}
const emptyPeriod = (year, currency = "USD", startMonth = null) => {
  const fm = startMonth || `${year}-09`;
  const [sy, sm] = fm.split("-").map(Number);
  // Default: 4 months (start + 3), capped at December (can't cross Jan 1)
  const endM = Math.min(sm + 3, 12);
  const tm = `${sy}-${String(endM).padStart(2, "0")}`;
  return { id: crypto.randomUUID(), _autoDefault: true, term_label: "", situation: "on_campus", situation_custom: "", housing_type: "school_housing", school_invoiced_amount: "", from_month: fm, to_month: tm, mode: "separate", basis: "monthly", food: "", housing: "", combined: "", note: "", docs: [], coa_currency: currency, coa_fx_rate: currency === "USD" ? 1.0 : null, coa_fx_date: null, coa_fx_override: false, half_time: true, enter_later: false };
};

/** The term that ends latest: the one a new term follows. */
const lastTerm = (periods) => {
  if (!periods?.length) return null;
  return [...periods].sort((a, b) => (a.to_month || "").localeCompare(b.to_month || "")).at(-1);
};
// ZEN-783 (Maryse, 17 Oct 2026): Add term starts from the previous term in the
// same academic year: living situation, separate or combined, per month or per
// term, the amounts and the currency. Never the dates, the school invoiced
// amount or the note. `_copied` is screen-only; buildSchoolsPayload never sends it.
const COPIED_AMOUNTS = ["food", "housing", "combined"];
export const copiedTermLine = (from) => `Copied from ${from} \u2014 change if different`;
export const copyPreviousTerm = (prev, fresh) => {
  if (!prev) return fresh;
  const cur = prev.coa_currency || fresh.coa_currency;
  const p = {
    ...fresh,
    situation: prev.situation || fresh.situation, situation_custom: prev.situation_custom || "", housing_type: prev.housing_type || fresh.housing_type,
    mode: prev.mode || "separate", basis: prev.basis || "monthly",
    food: prev.food ?? "", housing: prev.housing ?? "", combined: prev.combined ?? "",
    coa_currency: cur, coa_fx_rate: cur === "USD" ? 1.0 : null, coa_fx_date: null, coa_fx_override: false,
  };
  if (COPIED_AMOUNTS.some((k) => hasCoaAmount(p[k]))) {
    p._copied = { from: termMonthsShort(prev.from_month, prev.to_month), food: p.food, housing: p.housing, combined: p.combined };
  }
  return p;
};
const sameAmount = (a, b) => {
  const n = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
  return n(a) === n(b) || (Number.isNaN(n(a)) && Number.isNaN(n(b)));
};
/** The copied line stays only while every copied amount is untouched. */
const keepCopied = (pp, k, next) => (pp._copied && COPIED_AMOUNTS.includes(k) && !sameAmount(next, pp._copied[k]) ? undefined : pp._copied);

/** The month after the latest term ends: where Add term starts. */
const smartNextStart = (periods) => {
  const last = lastTerm(periods);
  if (!last?.to_month) return null;
  const [y, m] = last.to_month.split("-").map(Number);
  if (m === 12) return `${y + 1}-01`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
};
/** The term "Add term" appends to a year block. */
export const nextTermFor = (periods, schoolCurrency) => {
  const nextStart = smartNextStart(periods);
  const yr = nextStart ? parseInt(nextStart.split("-")[0]) : parseInt((periods?.[0]?.from_month || `${currentAcademicYear()}-01`).split("-")[0]);
  return copyPreviousTerm(lastTerm(periods), emptyPeriod(yr, schoolCurrency || "USD", nextStart));
};

/** Split a period crossing Jan 1 into two tax-year-bounded halves. */
function splitPeriodAtYearBoundary(p) {
  const [fy] = (p.from_month || "").split("-").map(Number);
  const [ty] = (p.to_month || "").split("-").map(Number);
  if (!fy || !ty || fy === ty) return [p]; // same year — no split needed
  // Convert period-basis to monthly before splitting. A blank field stays
  // blank — fabricating a 0 would make a docs-only term look like it carries
  // an explicit $0 once it round-trips through a save.
  const perMonth = (v) => {
    if (!hasCoaAmount(v)) return null;
    const n = parseFloat(v) || 0;
    if (p.basis !== "period") return n;
    const totalM = monthsBetween(p.from_month, p.to_month);
    return totalM > 0 ? Math.round((n / totalM) * 100) / 100 : n;
  };
  const monthlyFood = perMonth(p.food);
  const monthlyHousing = perMonth(p.housing);
  const monthlyCombined = perMonth(p.combined);
  // Prorate school_invoiced_amount by months per half
  const totalMonths = monthsBetween(p.from_month, p.to_month);
  const firstTo = `${fy}-12`;
  const secondFrom = `${fy + 1}-01`;
  const firstMonths = monthsBetween(p.from_month, firstTo);
  const secondMonths = monthsBetween(secondFrom, p.to_month);
  const rawInvoice = coaAmountOrNull(p.school_invoiced_amount);
  const prorate = totalMonths > 0 && rawInvoice > 0;
  const firstInvoice = prorate ? Math.round((rawInvoice * firstMonths / totalMonths) * 100) / 100 : rawInvoice;
  const secondInvoice = prorate ? Math.round((rawInvoice - firstInvoice) * 100) / 100 : rawInvoice;
  const shared = { situation: p.situation, situation_custom: p.situation_custom, housing_type: p.housing_type || "off_campus", mode: p.mode, basis: "monthly", food: monthlyFood, housing: monthlyHousing, combined: monthlyCombined, note: p.note, docs: p.docs || [], coa_currency: p.coa_currency, coa_fx_rate: p.coa_fx_rate, coa_fx_date: p.coa_fx_date, coa_fx_override: p.coa_fx_override, half_time: p.half_time !== false, enter_later: !!p.enter_later };
  const baseLbl = p.term_label || "";
  const first = { ...shared, term_label: baseLbl, school_invoiced_amount: firstInvoice, id: p.id || crypto.randomUUID(), from_month: p.from_month, to_month: firstTo };
  const second = { ...shared, term_label: baseLbl ? `${baseLbl} (cont.)` : "", school_invoiced_amount: secondInvoice, id: crypto.randomUUID(), from_month: secondFrom, to_month: p.to_month };
  return [first, second];
}
const emptySchool = () => ({ id: crypto.randomUUID(), school_name: "", student_school_id: "", school_529_code: "", country: "US", currency: "USD", start_date: "", end_date: "", expense_group: "post_secondary", notes: "", coa_by_year: [], archived: false });

/**
 * Counts COA document uploads still in flight, so Save can refuse to build a
 * payload from a period whose document has not landed in it yet — saving mid-
 * upload would drop the period and orphan the file.
 */
const CoaUploadsInFlight = React.createContext(null);

function StudentDialog({ open, onClose, student, onSaved, focusTarget }) {
  const uploadsInFlight = useRef(0);
  const [name, setName] = useState(""); const [nick, setNick] = useState(""); const [schools, setSchools] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(0); // accordion: which period is expanded (-1 = none)
  const { onDateValidation, hasDateErrors } = useDateErrors();
  // AOTC dialog state
  const aotc = useAotcReserve({ studentId: student?.id, active: open });
  React.useEffect(() => {
    if (!open) return;
    setName(student?.full_name || ""); setNick(student?.nickname || "");
    setSchools(student?.schools?.length ? student.schools.map((s) => ({
      ...s,
      expense_group: s.expense_group || student?.expense_group || "post_secondary",
      archived: !!s.archived,
      coa_by_year: (s.coa_by_year && s.coa_by_year.length)
        ? s.coa_by_year.map((y) => {
            const yNum = y.year || parseInt((y.label || "").slice(0, 4)) || currentAcademicYear();
            return ({
              label: y.label || (y.year != null ? String(y.year) : `${yNum - 1}\u2013${yNum}`),
              coverage_note: y.coverage_note || "",
              periods: (y.periods && y.periods.length)
                ? y.periods.map((p) => ({ id: p.id || crypto.randomUUID(), term_label: p.term_label || "", situation: p.situation || "other", situation_custom: p.situation_custom || "", housing_type: p.housing_type || "off_campus", school_invoiced_amount: p.school_invoiced_amount ?? "", from_month: p.from_month || `${yNum}-09`, to_month: p.to_month || `${yNum}-12`, mode: p.mode || "separate", basis: p.basis || "monthly", food: p.food ?? "", housing: p.housing ?? "", combined: p.combined ?? "", note: p.note || "", docs: p.docs || [], coa_currency: p.coa_currency || s.currency || "USD", coa_fx_rate: p.coa_fx_rate ?? ((!p.coa_currency || p.coa_currency === "USD") ? 1.0 : null), coa_fx_date: p.coa_fx_date || null, coa_fx_override: !!p.coa_fx_override, half_time: p.half_time !== false, enter_later: !!p.enter_later }))
                : [{ id: crypto.randomUUID(), situation: "other", situation_custom: "", from_month: `${yNum}-01`, to_month: `${yNum}-12`, mode: y.mode || "separate", basis: "period", food: y.food ?? "", housing: y.housing ?? "", combined: y.combined ?? "", note: y.note || "", coa_currency: s.currency || "USD", coa_fx_rate: (s.currency || "USD") === "USD" ? 1.0 : null, coa_fx_date: null, coa_fx_override: false }],
            });
          })
        : ((s.coa_food || s.coa_room_board || s.coa_combined)
            ? [{ label: `${currentAcademicYear() - 1}\u2013${currentAcademicYear()}`, periods: [{ id: crypto.randomUUID(), situation: "other", situation_custom: "", from_month: `${currentAcademicYear()}-01`, to_month: `${currentAcademicYear()}-12`, mode: s.coa_mode || "separate", basis: "period", food: s.coa_food ?? "", housing: s.coa_room_board ?? "", combined: s.coa_combined ?? "", note: "", coa_currency: s.currency || "USD", coa_fx_rate: (s.currency || "USD") === "USD" ? 1.0 : null, coa_fx_date: null }] }]
            : []),
    })) : [emptySchool()]);
    // Expand the first active period (or 0 if new)
    if (student?.schools?.length) {
      const firstActive = student.schools.findIndex(s => !s.archived);
      setExpandedIdx(firstActive >= 0 ? firstActive : 0);
    } else {
      setExpandedIdx(0);
    }
  }, [open, student]);

  // Focus the correct element when opened via URL param
  useEffect(() => {
    if (!open || !focusTarget) return;
    const timer = setTimeout(() => {
      if (focusTarget === "end-date") {
        // Find the first non-archived school with no end date
        const idx = schools.findIndex((s) => !s.archived && !s.end_date);
        const target = idx >= 0 ? idx : 0;
        const el = document.querySelector(`[data-testid="school-end-date-${target}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
          el.classList.add("ring-2", "ring-[#D97706]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#D97706]", "ring-offset-2"), 4000);
        }
      } else if (focusTarget === "add-school") {
        const el = document.querySelector('[data-testid="add-school-btn"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
          el.classList.add("ring-2", "ring-[#D97706]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#D97706]", "ring-offset-2"), 4000);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, focusTarget, schools.length]); // eslint-disable-line

  const setSc = (i, k, v) => setSchools((p) => p.map((s, idx) => {
    if (idx !== i) return s;
    const updated = { ...s, [k]: v };
    // Re-anchor auto-default terms when start_date changes
    if (k === "start_date" && v) {
      const startMo = v.slice(0, 7); // "YYYY-MM"
      const [sy, sm] = startMo.split("-").map(Number);
      const endM = Math.min(sm + 3, 12);
      const newTo = `${sy}-${String(endM).padStart(2, "0")}`;
      updated.coa_by_year = (updated.coa_by_year || []).map((y) => {
        const ps = y.periods || [];
        // Only re-anchor if the school has exactly one term and it's still auto-default
        if (ps.length === 1 && ps[0]._autoDefault) {
          return { ...y, periods: [{ ...ps[0], from_month: startMo, to_month: newTo }] };
        }
        return y;
      });
    }
    return updated;
  }));
  const setYr = (i, yi, k, v) => setSchools((p) => p.map((s, idx) => idx === i ? { ...s, coa_by_year: s.coa_by_year.map((y, j) => j === yi ? { ...y, [k]: v } : y) } : s));
  const addYr = (i) => setSchools((p) => p.map((s, idx) => {
    if (idx !== i) return s;
    const existingPeriods = (s.coa_by_year || []).flatMap((y) => (y.periods || []));
    if (existingPeriods.length === 0) {
      // No blocks yet — anchor to start_date (preserves existing first-block behavior)
      const startMo = s.start_date ? s.start_date.slice(0, 7) : null;
      const yr = startMo ? parseInt(startMo.split("-")[0]) : currentAcademicYear();
      return { ...s, coa_by_year: [...(s.coa_by_year || []), { label: "", periods: [emptyPeriod(yr, s.currency || "USD", startMo)] }] };
    }
    // Blocks exist — next September after the latest academic-year window
    const allMonths = existingPeriods.flatMap((pp) => [pp.from_month, pp.to_month]).filter(Boolean).sort();
    const latest = allMonths[allMonths.length - 1];
    const [ly, lm] = latest.split("-").map(Number);
    const latestWinStartY = lm < 9 ? ly - 1 : ly;
    const nextSep = `${latestWinStartY + 1}-09`;
    return { ...s, coa_by_year: [...(s.coa_by_year || []), { label: "", periods: [emptyPeriod(latestWinStartY + 1, s.currency || "USD", nextSep)] }] };
  }));
  const removeYr = (i, yi) => setSchools((p) => p.map((s, idx) => idx === i ? { ...s, coa_by_year: s.coa_by_year.filter((_, j) => j !== yi) } : s));
  const [confirmDelYr, setConfirmDelYr] = useState(null); // [schoolIdx, yearIdx]
  const setPd = (i, yi, pi, k, v) => setSchools((p) => applyPeriodField(p, i, yi, pi, k, v));
  const addPd = (i, yi) => setSchools((prev) => prev.map((s, idx) => {
    if (idx !== i) return s;
    return { ...s, coa_by_year: s.coa_by_year.map((y, j) => (j !== yi ? y : { ...y, periods: [...(y.periods || []), nextTermFor(y.periods, s.currency)] })) };
  }));
  const removePd = (i, yi, pi) => setSchools((p) => p.map((s, idx) => idx === i ? { ...s, coa_by_year: s.coa_by_year.map((y, j) => j === yi ? { ...y, periods: y.periods.filter((_, k2) => k2 !== pi) } : y) } : s));
  const [confirmDelSchool, setConfirmDelSchool] = useState(null); // index to confirm
  const delSchool = (i) => setConfirmDelSchool(i);
  const confirmDelSchoolAction = () => { setSchools((p) => p.filter((_, idx) => idx !== confirmDelSchool)); setConfirmDelSchool(null); };
  const save = async () => {
    if (!name) { toast.error("Name required"); return; }
    if (uploadsInFlight.current > 0) { toast.error("A document is still uploading — wait for it to finish, then save."); return; }
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    // Overlap check: no month may be covered by two terms within a school
    for (const s of schools) {
      if (!s.school_name) continue;
      const allPeriods = (s.coa_by_year || []).flatMap((y) => (y.periods || []));
      const ol = detectOverlaps(allPeriods);
      if (Object.keys(ol).length > 0) {
        const firstM = Object.keys(ol).sort()[0];
        const ps = ol[firstM];
        const names = ps.map((pp) => pp.term_label || guessTermLabel(pp.from_month, pp.to_month) || "a term");
        const hasDupes = names.length !== new Set(names).size;
        const displayNames = hasDupes
          ? ps.map((pp) => { const lbl = pp.term_label || guessTermLabel(pp.from_month, pp.to_month) || "a term"; return `${lbl} (${monthFullName(pp.from_month)?.split(" ")[0]}\u2013${monthFullName(pp.to_month)})`; })
          : names;
        toast.error(`${monthFullName(firstM)} is covered by both ${displayNames.join(" and ")} in ${s.school_name} — each month can belong to only one term. Fix the overlap before saving.`);
        return;
      }
    }
    const nothingYet = termsHoldingNothing(schools);
    if (nothingYet.length) { toast.error(nothingYet.join("\n"), { className: "whitespace-pre-line" }); return; }
    const payload = { full_name: name, nickname: nick, schools: buildSchoolsPayload(schools) };
    try {
      let savedId;
      if (student) { await api.put(`/students/${student.id}`, payload); savedId = student.id; }
      else { const res = await api.post("/students", payload); savedId = res.data.id; }
      // Save AOTC if applicable
      const dlgHasHigherEd = schools.some(sc => !sc.archived && (sc.expense_group || "post_secondary") === "post_secondary");
      if (dlgHasHigherEd && aotc.isDirty) {
        try {
          await aotc.save(savedId);
        } catch { toast.warning("Beneficiary saved, but AOTC setting could not be saved. Try again from the card."); }
      }
      toast.success("Saved"); onSaved(); onClose();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <CoaUploadsInFlight.Provider value={uploadsInFlight}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Beneficiary</DialogTitle><DialogDescription className="sr-only">Manage beneficiary details and school enrollments</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Full name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="student-name" className="h-10 w-full rounded-md border border-[#C2C9D1] px-3 text-sm" /></label>
          <label className="block"><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Nickname (shown everywhere)</span>
            <input value={nick} onChange={(e) => setNick(e.target.value)} data-testid="student-nickname" placeholder="auto from first name" className="h-10 w-full rounded-md border border-[#C2C9D1] px-3 text-sm" /></label>
        </div>
        {/* Expense group selector removed — type is now per education period */}
        <div className="mt-2 flex items-center justify-between"><span className="text-sm font-bold uppercase tracking-wide">EDUCATION PERIODS</span>
          <Btn variant="ghost" className="h-8" onClick={() => { setSchools((p) => [...p, emptySchool()]); setExpandedIdx(schools.length); }} data-testid="add-school-btn"><Plus size={14} weight="bold" /> Add period</Btn></div>
        <div className="space-y-2">
          {/* Sort: active periods first, then archived */}
          {schools
            .map((s, origIdx) => ({ s, origIdx }))
            .sort((a, b) => (a.s.archived ? 1 : 0) - (b.s.archived ? 1 : 0))
            .map(({ s, origIdx: i }) => {
            const isPeriodPS = (s.expense_group || "post_secondary") === "post_secondary";
            const periodTypeLabel = EXPENSE_GROUPS.find(g => g.key === (s.expense_group || "post_secondary"))?.label || "Higher Education";
            const isExpanded = expandedIdx === i;
            const displayName = s.school_name || "New period";
            const dateRange = s.start_date && s.end_date ? `${fmtDate(s.start_date)} – ${fmtDate(s.end_date)}` : s.start_date ? `from ${fmtDate(s.start_date)}` : "";
            return (
            <div key={s.id} className={`rounded-lg border ${isExpanded ? "border-[#034AA2]/40" : "border-[#C2C9D1]"} ${s.archived ? "opacity-60" : ""}`} data-testid={`school-row-${i}`}>
              {/* Collapsed summary row — always visible */}
              <button type="button" onClick={() => setExpandedIdx(isExpanded ? -1 : i)} className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left" data-testid={`period-summary-${i}`}>
                <Buildings size={16} weight="bold" className={s.archived ? "text-[#6b7280]" : "text-[#034AA2]"} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-[#0A0A0A]">{displayName}</div>
                  <div className="truncate text-[11px] text-[#6b7280]">{periodTypeLabel}{dateRange ? ` · ${dateRange}` : ""}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.archived ? "bg-[#6b7280]/10 text-[#6b7280]" : "bg-[#077A39]/10 text-[#077A39]"}`} data-testid={`period-status-${i}`}>
                  {s.archived ? "Archived" : "Active"}
                </span>
                <span className="shrink-0 text-[#6b7280] transition-transform" data-testid={`period-chevron-${i}`}>
                  {isExpanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                </span>
              </button>
              {/* Expanded editable fields */}
              {isExpanded && (
              <div className="border-t border-[#C2C9D1] px-3 pb-3 pt-3">
              {/* Guarded type picker */}
              <PeriodTypePicker school={s} index={i} setSc={setSc} periodTypeLabel={periodTypeLabel} />
              {/* Type-adaptive fields */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {isPeriodPS ? (
                  <>
                    {/* School cluster */}
                    <div className="col-span-1 sm:col-span-2">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[3fr_1fr]">
                        <SchoolAutocomplete
                          value={s.school_name}
                          onChange={(v) => setSc(i, "school_name", v)}
                          onSelect={(match) => {
                            setSc(i, "school_name", match.name);
                            setSc(i, "school_529_code", match.school_code);
                            setSc(i, "country", normalizeCountryCode(match.country));
                          }}
                          testid={`school-name-${i}`}
                        />
                        <In label="529 school code" v={s.school_529_code} on={(v) => setSc(i, "school_529_code", v.toUpperCase().trim())} test={`school-529-code-${i}`} />
                      </div>
                      {/* Eligibility badge — full width under school row */}
                      {s.school_name && s.school_name.length >= 2 && (
                        <div className="mt-1">
                          <SchoolEligibilityBadgeWrapper schoolCode={s.school_529_code} schoolName={s.school_name} />
                        </div>
                      )}
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">Currency</span>
                          <CurrencySelect value={s.currency} onChange={(v) => setSc(i, "currency", v)} testid={`school-currency-${i}`} /></label>
                        <In label="Country" v={s.country} on={(v) => setSc(i, "country", v)} onBlur={() => { if (s.country) setSc(i, "country", normalizeCountryCode(s.country)); }} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Non-Higher-Ed: school/program name only */}
                    <div className="col-span-1 sm:col-span-2">
                      <In label="School / program name" v={s.school_name} on={(v) => setSc(i, "school_name", v)} test={`school-name-${i}`} />
                    </div>
                  </>
                )}
                {/* Dates */}
                <div className="col-span-1 sm:col-span-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <In label="Start date" type="date" v={s.start_date} on={(v) => setSc(i, "start_date", v)} onValidation={onDateValidation(`start-${i}`)} preset="completionDate" />
                    <In label="Expected completion date" type="date" v={s.end_date} on={(v) => setSc(i, "end_date", v)} test={`school-end-date-${i}`} preset="completionDate" onValidation={onDateValidation(`end-${i}`)} />
                  </div>
                  <p className="mt-1 text-[10px] text-[#6b7280]">Used to project how long this 529 needs to last. Without it, this beneficiary is excluded from projections.</p>
                </div>
                {!s.start_date && (s.coa_by_year || []).some((y) => (y.periods || []).length > 0) && (() => {
                  const earliest = (s.coa_by_year || []).flatMap((y) => (y.periods || [])).map((p) => p.from_month).filter(Boolean).sort()[0];
                  return earliest ? (
                    <div className="col-span-1 sm:col-span-2 -mt-1 flex items-center gap-2 text-[10px] text-[#6b7280]">
                      Suggested from your first term: {monthFullName(earliest)}
                      <button type="button" onClick={() => setSc(i, "start_date", `${earliest}-01`)} className="rounded bg-[#034AA2]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#034AA2] hover:bg-[#034AA2]/20" data-testid={`school-start-suggest-${i}`}>Apply</button>
                    </div>
                  ) : null;
                })()}
                {/* Student ID + Notes — two-column row, Higher Education only */}
                {isPeriodPS && (
                  <div className="col-span-1 sm:col-span-2">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <In label="Student ID at this school · optional" v={s.student_school_id} on={(v) => setSc(i, "student_school_id", v)} test={`school-id-${i}`} />
                      <NotesField className="sm:col-span-2" value={s.notes} onChange={(v) => setSc(i, "notes", v)} testid={`school-notes-${i}`} />
                    </div>
                  </div>
                )}
              </div>
              {/* Housing & Food 529 caps — Higher Education only */}
              {isPeriodPS && <div className="col-span-1 sm:col-span-2 mt-2 rounded-lg bg-[#F4F7FB] p-2.5">
                <div className="relative mb-1 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#034AA2]">Housing &amp; food 529 caps (USD) <InfoTip testid="coa-guidance-tooltip" width="w-80"><b className="block mb-1.5 text-xs text-[#0A0A0A]">Setting up Cost of Attendance (COA)</b><ul className="space-y-1.5 list-disc pl-3.5"><li>Only <b>room &amp; board</b> is capped by 529 rules&nbsp;&mdash; tuition, fees, books &amp; supplies are not. Enter only the housing + food allowance here.</li><li>The qualifying amount is the <b>greater of</b> your school&apos;s COA allowance or actual school-housing charges (IRS Pub 970). Off-campus beneficiaries are capped at the COA allowance; beneficiaries in school-owned housing may claim the actual invoiced amount even if it exceeds the COA.</li><li>Find it on your school&apos;s published &ldquo;Cost of Attendance&rdquo; (financial-aid site or registrar). Enter the allowance per living situation.</li><li>Start a new <b>term</b> only when something changes: living situation, the school&apos;s COA rate or meal plan, or half-time status. A term also can&apos;t cross Jan&nbsp;1 (it auto-splits). Otherwise it&apos;s fine to combine consecutive months into one term&nbsp;&mdash; one Jan&ndash;Aug term with the same living arrangement is simpler than three.</li><li>Enter the allowance per <b>month</b> (preferred&nbsp;&mdash; handles partial enrollment) or as a term total. Only include months the beneficiary is enrolled.</li><li>Mark &ldquo;Enrolled at least half-time?&rdquo;&nbsp;&mdash; room &amp; board only qualifies for 529 at &ge;&nbsp;half-time.</li><li>The app totals each year&apos;s monthly allowances to set the annual cap, and flags spending that exceeds it.</li></ul></InfoTip></span>
                  <Btn variant="ghost" className="h-7" onClick={() => addYr(i)} data-testid={`coa-add-year-${i}`}><Plus size={12} weight="bold" /> Add new academic year</Btn>
                </div>
                <p className="mb-2 text-[11px] text-[#6b7280]">Enter your school&apos;s published room &amp; board (housing + food) allowance here — it&apos;s the only cost 529 rules cap. See <b>ⓘ</b> for how to set up terms.</p>
                {(s.coa_by_year || []).length === 0 && <div className="text-[11px] text-[#6b7280]">No caps yet — click &ldquo;Add new academic year&rdquo;.</div>}
                {/* Overlap errors (across all year blocks for this school) */}
                {(() => {
                  const allP = (s.coa_by_year || []).flatMap((y) => (y.periods || []));
                  const ol = detectOverlaps(allP);
                  if (!Object.keys(ol).length) return null;
                  const firstM = Object.keys(ol).sort()[0];
                  const ps = ol[firstM];
                  const names = ps.map((pp) => pp.term_label || guessTermLabel(pp.from_month, pp.to_month) || "a term");
                  const hasDupes = names.length !== new Set(names).size;
                  const displayNames = hasDupes
                    ? ps.map((pp) => { const lbl = pp.term_label || guessTermLabel(pp.from_month, pp.to_month) || "a term"; return `${lbl} (${monthFullName(pp.from_month)?.split(" ")[0]}\u2013${monthFullName(pp.to_month)})`; })
                    : names;
                  return (
                    <div className="mb-2 rounded-md border border-[#F01919]/40 bg-[#FFF5F5] px-3 py-2 text-xs text-[#991B1B]" data-testid={`coa-overlap-error-${i}`}>
                      <b>Overlap:</b> {monthFullName(firstM)} is covered by both {displayNames.join(" and ")} — each month can belong to only one term. Fix this before saving.
                    </div>
                  );
                })()}
                <YearBlockList school={s} schoolIdx={i} studentId={student?.id} setYr={setYr} addPd={addPd} removePd={removePd} setPd={setPd} removeYr={removeYr} confirmDelYr={confirmDelYr} setConfirmDelYr={setConfirmDelYr} smartNextStart={smartNextStart} />
              </div>}
              {/* Archive + Delete */}
              <div className="mt-2 border-t border-[#C2C9D1] pt-3">
                <ArchivePeriodAction school={s} index={i} schools={schools} setSc={setSc} periodTypeLabel={periodTypeLabel} />
                <div className="mt-2 flex justify-end">
                  {(() => {
                    const schoolHasData = isSchoolSubstantive(s);
                    if (schoolHasData) return (
                      <span className="group/sdel relative inline-flex items-center gap-1 text-xs text-[#C2C9D1] cursor-not-allowed" data-testid={`school-delete-disabled-${i}`}>
                        <Trash size={12} weight="bold" className="inline" />
                        <span>Delete (added in error)</span>
                        <span className="pointer-events-none absolute right-0 top-full z-50 mt-1 w-64 rounded-md border border-[#C2C9D1] bg-white px-2.5 py-1.5 text-[10px] font-normal text-[#6b7280] shadow-lg opacity-0 group-hover/sdel:opacity-100">This period has data — archive it instead, or delete its terms first. Delete is only for periods added by mistake.</span>
                      </span>
                    );
                    return (
                      <button type="button" onClick={() => delSchool(i)} className="text-xs font-semibold text-[#F01919] hover:underline" data-testid={`school-delete-${i}`}><Trash size={12} weight="bold" className="mr-1 inline" />Delete (added in error)</button>
                    );
                  })()}
                </div>
              </div>
              </div>
              )}
            </div>
            );
          })}
        </div>
        {/* AOTC election — beneficiary-level, only for Higher-Education */}
        {schools.some(sc => !sc.archived && (sc.expense_group || "post_secondary") === "post_secondary") && (
          <div className="mt-4" data-testid="dialog-aotc-section">
            <AotcReserveFields form={aotc} dialogName={name} />
            {aotc.elig === "not_sure" && !aotc.loading && (
              <p className="mt-2 text-[11px] text-[#034AA2]"><b>Not sure?</b> The AOTC is worth up to <b>$2,500</b> per student per year. <Link to="/help?article=aotc" className="font-semibold underline">Learn more in the Help Center</Link></p>
            )}
          </div>
        )}
        <DeleteDialog open={confirmDelSchool !== null} onClose={() => setConfirmDelSchool(null)} onConfirm={confirmDelSchoolAction} noun="school" testid="school-delete" />
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} data-testid="save-student">Save</Btn></DialogFooter>
      </DialogContent>
      </CoaUploadsInFlight.Provider>
    </Dialog>
  );
}
// A school date stored the old way, as a month with no day ("2024-09"). The box
// shows the month and leaves it alone unless a full date is chosen. (ZEN-316)
export const isStoredMonth = (v) => /^\d{4}-\d{2}$/.test(v || "");
export const keepMonthUnlessDated = (on) => (next) => { if (next) on(next); };

function In({ label, v, on, type = "text", test, preset, onBlur, onValidation }) {
  if (type === "date") {
    const month = isStoredMonth(v);
    return <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">{label}</span>
      <UnifiedDateInput value={month ? "" : (v ?? "")} onChange={month ? keepMonthUnlessDated(on) : on} placeholder={month ? monthFullName(v) : undefined} testid={test} hideToday preset={preset} onValidation={onValidation} /></label>;
  }
  return <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">{label}</span>
    <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} onBlur={onBlur} data-testid={test} className="h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" /></label>;
}

/** Per-period archive action with light-confirm dialog. */
function ArchivePeriodAction({ school, index, setSc, periodTypeLabel }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (school.archived) {
    return (
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setSc(index, "archived", false)} className="text-xs font-semibold text-[#077A39] hover:underline" data-testid={`period-unarchive-${index}`}>
          <ArrowCounterClockwise size={12} weight="bold" className="mr-1 inline" />Reactivate this {periodTypeLabel} period
        </button>
      </div>
    );
  }
  return (
    <div>
      {!confirmOpen ? (
        <button type="button" onClick={() => setConfirmOpen(true)} className="text-xs font-semibold text-[#6b7280] hover:underline" data-testid={`period-archive-${index}`}>
          <Archive size={12} weight="bold" className="mr-1 inline" />Archive this {periodTypeLabel} period
        </button>
      ) : (
        <div className="rounded-md border border-[#C2C9D1] bg-[#F4F7FB] px-3 py-2.5">
          <p className="text-xs font-medium text-[#374151]">Archive this {periodTypeLabel} period?</p>
          <p className="mt-1 text-[11px] leading-relaxed text-[#6b7280]">Its records stay in your reports; it just stops counting toward active caps and Overview going forward. Your other periods aren&apos;t affected.</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => setConfirmOpen(false)} className="rounded border border-[#C2C9D1] px-2.5 py-1 text-xs font-semibold text-[#374151] hover:bg-[#E9E9EB]" data-testid={`period-archive-cancel-${index}`}>Cancel</button>
            <button type="button" onClick={() => { setSc(index, "archived", true); setConfirmOpen(false); }} className="rounded bg-[#6b7280] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#4B5563]" data-testid={`period-archive-confirm-${index}`}>Archive</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Guarded use-type picker: shows selected type as read-only pill + "Change type" button.
 * If the period has attached data (ref_count > 0), shows a consequence dialog with CHANGE gate.
 * If N==0, shows a neutral decision dialog. */
function PeriodTypePicker({ school, index, setSc, periodTypeLabel }) {
  const [showOptions, setShowOptions] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const currentGroup = school.expense_group || "post_secondary";
  const refCount = school.ref_count || 0;

  // When the period is brand new (no name, no data), show options directly
  const isNew = !school.school_name && refCount === 0;
  if (isNew || showOptions) {
    return (
      <div className="mb-3" data-testid={`period-type-selector-${index}`}>
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">529 qualified use type</span>
        <div className="flex flex-wrap gap-1.5">
          {EXPENSE_GROUPS.map((g) => (
            <button key={g.key} type="button" onClick={() => { setSc(index, "expense_group", g.key); if (!isNew) setShowOptions(false); }}
              data-testid={`period-type-${index}-${g.key}`}
              className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${currentGroup === g.key ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3" data-testid={`period-type-selector-${index}`}>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">529 qualified use type</span>
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-[#034AA2] px-2.5 py-1.5 text-[11px] font-semibold text-white" data-testid={`period-type-${index}-${currentGroup}`}>{periodTypeLabel}</span>
        <button type="button" onClick={() => { if (refCount > 0) setConfirmOpen(true); else setConfirmOpen(true); }}
          className="text-[11px] font-semibold text-[#034AA2] hover:underline" data-testid={`period-change-type-${index}`}>
          Change type
        </button>
      </div>
      <ChangeTypeConfirmDialog
        open={confirmOpen}
        refCount={refCount}
        periodTypeLabel={periodTypeLabel}
        index={index}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); setShowOptions(true); }}
      />
    </div>
  );
}

/** Adaptive confirmation dialog for changing a period's 529 use type.
 *  N>0: amber consequence + CHANGE type-gate + green Cancel / red action.
 *  N==0: neutral blue decision dialog. */
function ChangeTypeConfirmDialog({ open, refCount, periodTypeLabel, index, onClose, onConfirm }) {
  const [typed, setTyped] = React.useState("");
  React.useEffect(() => { if (open) setTyped(""); }, [open]);
  const gateOk = typed.trim().toUpperCase() === "CHANGE";
  const hasRecords = refCount > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid={`period-change-type-dialog-${index}`}>
        <DialogHeader>
          <DialogTitle>Change 529 qualified use type</DialogTitle>
          <DialogDescription className="sr-only">Confirm change to the period use type</DialogDescription>
        </DialogHeader>

        {hasRecords ? (
          <>
            <div className="rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-3 py-2.5" data-testid={`period-change-type-amber-${index}`}>
              <p className="text-xs leading-relaxed text-[#92400E]">
                <span className="font-semibold">{refCount}</span> record{refCount !== 1 ? "s" : ""} (expenses and 529 distributions) {refCount !== 1 ? "are" : "is"} tied to this <span className="font-semibold">{periodTypeLabel}</span> period. Changing the type recategorizes them and recomputes your caps — review those 529 distributions afterward.
              </p>
            </div>
            <div className="mt-2">
              <label className="block text-xs font-semibold text-[#374151]">
                Type <span className="rounded bg-[#F4F7FB] px-1.5 py-0.5 font-mono text-[11px] font-bold tracking-wider text-[#1f2937]">CHANGE</span> to confirm
              </label>
              <input
                autoFocus
                type="text"
                className="mt-1 w-full rounded-md border border-[#C2C9D1] px-3 py-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-1 focus:ring-[#034AA2]"
                placeholder="CHANGE"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid={`period-change-type-gate-${index}`}
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-[#6b7280]">No records are tied to this period yet. Changing the type just updates which caps apply.</p>
        )}

        <DialogFooter className="mt-3 gap-2">
          {hasRecords ? (
            <>
              {/* Green Cancel (safe) + Red action (consequence) */}
              <button onClick={onClose} className="flex-1 rounded-md bg-[#077A39] px-4 py-2 text-sm font-bold text-white hover:bg-[#066430]" data-testid={`period-change-type-cancel-${index}`}>Cancel</button>
              <button onClick={onConfirm} disabled={!gateOk} className="flex-1 rounded-md bg-[#F01919] px-4 py-2 text-sm font-bold text-white hover:bg-[#D81414] disabled:opacity-40" data-testid={`period-change-type-proceed-${index}`}>Change type</button>
            </>
          ) : (
            <>
              {/* Neutral Cancel + Blue action */}
              <button onClick={onClose} className="flex-1 rounded-md border border-[#C2C9D1] bg-white px-4 py-2 text-sm font-bold text-[#374151] hover:bg-[#F4F7FB]" data-testid={`period-change-type-cancel-${index}`}>Cancel</button>
              <button onClick={onConfirm} className="flex-1 rounded-md bg-[#034AA2] px-4 py-2 text-sm font-bold text-white hover:bg-[#023A82]" data-testid={`period-change-type-proceed-${index}`}>Change type</button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Wrapper that checks the directory for badge display.
 * Single source of truth: keyed on the 529 school code alone.
 * Both picked-from-autocomplete and hand-typed codes go through the same lookup.
 * Editing the display name does NOT invalidate a still-valid code.
 */
function SchoolEligibilityBadgeWrapper({ schoolCode, schoolName }) {
  const [lookupResult, setLookupResult] = useState(null);
  const [checked, setChecked] = useState(false);

  // Always derive eligibility from code lookup — no shortcut for autocomplete picks
  useEffect(() => {
    setChecked(false);
    setLookupResult(null);
    const normalized = (schoolCode || "").trim().toUpperCase();
    if (!normalized || normalized.length < 3) { setChecked(true); return; }
    let cancelled = false;
    api.get(`/schools-directory/lookup?code=${encodeURIComponent(normalized)}`)
      .then(({ data }) => { if (!cancelled) { setLookupResult(data); setChecked(true); } })
      .catch(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, [schoolCode]);

  if (!checked) return null;
  if (lookupResult) {
    return <SchoolEligibilityBadge matched={true} edition={lookupResult.edition} />;
  }
  // No code or no match — show not-found note only if there's a school name typed
  if (schoolName && schoolName.length >= 2) {
    return <SchoolNotFoundNote />;
  }
  return null;
}

/** Lightweight badge for the student card — checks if a school_529_code exists in the directory. */
function SchoolCodeBadge({ code }) {
  const { data } = useQuery({
    queryKey: ["school-lookup", code],
    queryFn: async () => (await api.get(`/schools-directory/lookup?code=${encodeURIComponent(code)}`)).data,
    enabled: !!code && code.trim().length >= 3,
    staleTime: 5 * 60 * 1000,
  });
  if (!data) return null;
  return (
    <div className="mt-1 flex items-center gap-1 text-[10px] text-[#065F28]" data-testid="school-card-eligible-badge">
      <CheckCircle size={12} weight="fill" className="text-[#077A39]" />
      529-eligible (Federal School Code List)
    </div>
  );
}


/**
 * A cost typed either per month or as the period total; the other box is
 * worked out from it. The stored figure is whichever box was typed in (`basis`).
 *
 * Leaving the worked-out box without typing must not adopt its rounded figure:
 * 1,000.00 over 3 months shows 333.33 a month, and storing that would turn the
 * period into 999.99. So a change that only repeats what the box already
 * showed is ignored.
 */
export function TwoWayMoneyIn({ label, val, basis, months, onVal, onBasis, test, currency, monthlyLabel, periodLabel }) {
  const raw = parseFloat(val) || 0;
  const empty = val === "" || val == null;
  const curBasis = basis || "monthly";
  const shown = {
    monthly: curBasis === "monthly" ? (val ?? "") : empty ? "" : (months > 0 ? raw / months : 0).toFixed(2),
    period: curBasis === "period" ? (val ?? "") : empty ? "" : (raw * months).toFixed(2),
  };
  const typed = (box) => (v) => {
    if (box !== curBasis && v === shown[box]) return;
    onVal(v); onBasis(box);
  };
  const mLabel = monthlyLabel || "Per month";
  const pLabel = periodLabel || `Period total${months > 0 ? ` (${months} mo)` : ""}`;
  return (
    <div>
      {label ? <span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">{label}</span> : null}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="mb-0.5 block text-[10px] font-medium text-[#9aa1a9]">{mLabel}</span>
          <MoneyInput currency={currency} className="!h-9" value={shown.monthly} onChange={typed("monthly")} testid={`${test}-monthly`} />
        </div>
        <div>
          <span className="mb-0.5 block text-[10px] font-medium text-[#9aa1a9]">{pLabel}</span>
          <MoneyInput currency={currency} className="!h-9" value={shown.period} onChange={typed("period")} testid={`${test}-period`} />
        </div>
      </div>
    </div>
  );
}

/** What the school actually invoiced for room and board this period. */
function SchoolInvoicedInput({ val, onChange, months, currency, testid, label }) {
  const displayLabel = label || `School invoiced R&B${months > 0 ? ` (${months} mo)` : ""}`;
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-medium text-[#9aa1a9]">{displayLabel}</span>
      <MoneyInput currency={currency} className="!h-9" value={val ?? ""} onChange={onChange}
        testid={testid} placeholder="housing + meal plan only" />
    </label>
  );
}



/** True when a COA money field carries a real number the owner entered — an explicit "0" counts, a blank or a half-typed "." does not. */
export const hasCoaAmount = (v) => v !== "" && v != null && Number.isFinite(parseFloat(v));
/** Serializes a COA money field: a real entered value (including 0) becomes a number, anything else stays null. */
export const coaAmountOrNull = (v) => (hasCoaAmount(v) ? parseFloat(v) : null);
/** Every money field the period's current mode and housing type actually use — the mode toggles hide the others but keep their values. */
export const activeCoaFields = (p) => [
  ...((p.mode || "separate") === "combined" ? [p.combined] : [p.food, p.housing]),
  ...((p.housing_type || "off_campus") === "school_housing" ? [p.school_invoiced_amount] : []),
];
/** True when the fields the period currently uses carry a dollar figure — what the owner can actually see and act on. */
export const hasActiveCoaAmount = (p) => activeCoaFields(p).some(hasCoaAmount);
/** True when ANY money field holds a value, including one a mode toggle currently hides — never throw away what the owner typed. */
export const hasAnyCoaAmount = (p) => [p.food, p.housing, p.combined, p.school_invoiced_amount].some(hasCoaAmount);
/**
 * A term is substantive if the owner put anything in it: an amount, a label, a
 * note, a document, an "Enter later" tick, or a below-half-time switch (that
 * term rightly holds no room and board cost). ZEN-781: a term that is none of
 * these is no longer dropped on save — the save is refused and says which
 * school and year (termsHoldingNothing).
 */
export const isTermSubstantive = (p) => !!(hasAnyCoaAmount(p) || hasVisibleText(p.term_label) || hasVisibleText(p.note) || (p.docs?.length > 0) || p.enter_later || p.half_time === false);
/** Text a person can actually see: spaces and invisible characters alone do not count. */
// The class, not a list of characters (review round 2): text counts only if it
// holds a letter, digit, punctuation mark or symbol, after removing the blank
// "filler" letters some scripts define (Hangul fillers, the braille blank).
export const hasVisibleText = (v) => typeof v === "string"
  && /[\p{L}\p{N}\p{P}\p{S}]/u.test(v.replace(/[\u115F\u1160\u3164\uFFA0\u2800]/g, ""));
/** A term ticked "Enter later" that still has no amount: kept, flagged on the Overview, its limit "Not entered yet". */
export const termWaitsForAmount = (p) => !!p.enter_later && p.half_time !== false && !hasActiveCoaAmount(p);
export { academicYearLabel };
/**
 * ZEN-781 — the save refusal, approved word for word by Maryse on 16 Oct 2026.
 * One line per school and year that holds a term with nothing in it. Before
 * this, such a term (and its whole year) vanished on save with no message.
 */
export const termsHoldingNothing = (schools) => {
  const lines = [];
  for (const s of schools || []) {
    if (!s.school_name) continue;
    for (const y of s.coa_by_year || []) {
      if ((y.periods || []).some((p) => !isTermSubstantive(p))) {
        const yr = academicYearLabel(y.periods);
        lines.push(`${s.school_name}${yr ? ` ${yr}` : ""}: a term has no cost of attendance. Enter it with its supporting document, or tick Enter later.`);
      }
    }
  }
  return lines;
};

/**
 * The single rule for "this term still owes the owner a dollar figure" — used
 * both by the reminder on the term itself and by the year's month accounting,
 * so the two can never tell the owner different things.
 * Excluded on purpose: a blank term (the save refuses it, ZEN-781)
 * and a below-half-time term (room & board does not qualify at all below half
 * time, so asking for a COA figure there would be wrong advice).
 */
export const termOwesAmount = (p) => isTermSubstantive(p) && !hasActiveCoaAmount(p) && p.half_time !== false;

/**
 * Splits an academic year's months into three states, because they need
 * different answers from the owner:
 *   covered   — a term with a dollar figure spans it; it counts toward the cap.
 *   gap       — no term spans it at all; usually "they weren't enrolled then".
 *   unpriced  — a term spans it but still owes a figure, so it contributes
 *               nothing. Enrolled, but the owner has a number left to enter.
 * A coverage note explains an absence of enrolment, so it may silence a gap but
 * must never silence an unpriced month.
 */
export const coaMonthCoverage = (periods, stripMonths) => {
  const coverMap = {};
  (periods || []).forEach((p, pi) => {
    expandMonths(p.from_month, p.to_month).forEach((m) => {
      if (!coverMap[m]) coverMap[m] = [];
      coverMap[m].push(pi);
    });
  });
  const gapMonths = (stripMonths || []).filter((m) => !coverMap[m]);
  const unpricedMonths = (stripMonths || []).filter((m) => {
    const pis = coverMap[m] || [];
    if (!pis.length) return false;
    // Any priced term covering the month settles it.
    if (pis.some((pi) => hasActiveCoaAmount(periods[pi]))) return false;
    return pis.some((pi) => termOwesAmount(periods[pi]));
  });
  return { coverMap, gapMonths, unpricedMonths };
};
/** A school enrollment is substantive if any term in any year block is substantive. */
const isSchoolSubstantive = (s) => (s.coa_by_year || []).some((y) => (y.periods || []).some(isTermSubstantive));

/**
 * Builds the `schools` array for the student save payload. A period is kept
 * only when isTermSubstantive(p) is true — ZEN-47: this used to check for a
 * dollar amount only, which silently deleted periods that held nothing but
 * an uploaded document. ZEN-781: save() refuses before this runs when any
 * term holds nothing, so the filter no longer drops anything the owner added.
 */
export const buildSchoolsPayload = (schools) => schools.filter((s) => s.school_name).map((s) => {
  const { _directory_match, ...rest } = s;
  return {
    ...rest,
    expense_group: s.expense_group || "post_secondary",
    archived: !!s.archived,
    coa_food: null, coa_room_board: null, coa_combined: null,
    coa_by_year: (s.coa_by_year || []).map((y) => ({
      label: yearSpanLabel(y.periods) || `${currentAcademicYear() - 1}–${currentAcademicYear()}`,
      coverage_note: y.coverage_note || "",
      periods: (y.periods || []).filter(isTermSubstantive).flatMap((p) => {
        const base = {
          id: p.id, term_label: p.term_label || "", situation: p.situation || "other", situation_custom: p.situation === "other" ? (p.situation_custom || "") : "",
          housing_type: p.housing_type || "off_campus",
          school_invoiced_amount: coaAmountOrNull(p.school_invoiced_amount),
          from_month: p.from_month, to_month: p.to_month, mode: p.mode || "separate", basis: p.basis || "monthly",
          food: coaAmountOrNull(p.food), housing: coaAmountOrNull(p.housing), combined: coaAmountOrNull(p.combined),
          note: p.note || "",
          docs: p.docs || [],
          coa_currency: p.coa_currency || "USD",
          coa_fx_rate: p.coa_fx_rate ? parseFloat(p.coa_fx_rate) : ((p.coa_currency || "USD") === "USD" ? 1.0 : null),
          coa_fx_date: p.coa_fx_date || null, coa_fx_override: !!p.coa_fx_override,
          half_time: p.half_time !== false,
          enter_later: termWaitsForAmount(p),
        };
        return splitPeriodAtYearBoundary(base);
      }),
    })).filter((y) => y.periods.length),
  };
});

function YearBlockList({ school: s, schoolIdx: i, studentId, setYr, addPd, removePd, setPd, removeYr, confirmDelYr, setConfirmDelYr, smartNextStart }) {
  const years = React.useMemo(() => s.coa_by_year || [], [s.coa_by_year]);
  const sorted = years.map((y, yi) => ({ y, yi, winStartY: blockWindowStartYear(y.periods) || 0 }))
    .sort((a, b) => b.winStartY - a.winStartY);
  // Year-block expand/collapse
  const [expanded, setExpanded] = useState(() => {
    if (!sorted.length) return new Set();
    return new Set([sorted[0].yi]);
  });
  const prevYearCount = useRef(years.length);
  useEffect(() => {
    if (years.length > prevYearCount.current) {
      const newSorted = years.map((y, yi) => ({ yi, winStartY: blockWindowStartYear(y.periods) || 0 }))
        .sort((a, b) => b.winStartY - a.winStartY);
      if (newSorted.length) setExpanded(new Set([newSorted[0].yi]));
    } else if (years.length < prevYearCount.current) {
      setExpanded((prev) => { const next = new Set(); prev.forEach((idx) => { if (idx < years.length) next.add(idx); }); return next; });
    }
    prevYearCount.current = years.length;
  }, [years]);
  const toggleYear = (yi) => setExpanded((prev) => { const next = new Set(prev); next.has(yi) ? next.delete(yi) : next.add(yi); return next; });

  // Term open state — controlled: { [yi]: pi } — which period is open per year block
  const [termOpenMap, setTermOpenMap] = useState(() => {
    const m = {};
    years.forEach((y, yi) => { const ps = y.periods || []; if (ps.length) m[yi] = ps.length - 1; });
    return m;
  });
  // Track per-year period counts to detect additions
  const prevTermCounts = useRef(years.map((y) => (y.periods || []).length));
  useEffect(() => {
    const newCounts = years.map((y) => (y.periods || []).length);
    newCounts.forEach((cnt, yi) => {
      const prev = prevTermCounts.current[yi] || 0;
      if (cnt > prev) {
        // New term added in year yi — expand the newest, collapse siblings
        setTermOpenMap((m) => ({ ...m, [yi]: cnt - 1 }));
      }
    });
    prevTermCounts.current = newCounts;
  }, [years.map((y) => (y.periods || []).length).join(",")]); // eslint-disable-line
  const toggleTerm = (yi, pi) => setTermOpenMap((m) => ({ ...m, [yi]: m[yi] === pi ? -1 : pi }));

  if (!sorted.length) return null;
  return (
    <div className="space-y-3">
      {confirmDelYr && confirmDelYr[0] === i && (
        <DeleteDialog open onClose={() => setConfirmDelYr(null)} onConfirm={() => { removeYr(i, confirmDelYr[1]); setConfirmDelYr(null); }} noun="academic year" testid={`coa-remove-year-${i}-${confirmDelYr[1]}`} />
      )}
      {sorted.map(({ y, yi }) => {
        const periods = y.periods || [];
        const isOpen = expanded.has(yi);
        const stripMonths = academicYearWindow(periods);
        const { coverMap, gapMonths, unpricedMonths } = coaMonthCoverage(periods, stripMonths);
        const gapNames = gapMonths.map((m) => monthFullName(m));
        const unpricedNames = unpricedMonths.map((m) => monthFullName(m));
        const winStartY = blockWindowStartYear(periods);
        const windowMonths = winStartY != null ? fixedAcademicWindow(winStartY) : [];
        const windowEnd = windowMonths.length ? windowMonths[windowMonths.length - 1] : null;
        const coveredCount = windowMonths.filter((m) => coverMap[m]?.length > 0).length;
        const windowFull = windowMonths.length > 0 && coveredCount === windowMonths.length;
        const nextStart = smartNextStart(periods);
        const nextInWindow = nextStart && windowEnd && nextStart <= windowEnd;
        const nextYearLabel = winStartY != null ? `${winStartY + 1}\u2013${winStartY + 2}` : "";
        const spanLabel = yearSpanLabel(periods) || (winStartY != null ? `${winStartY}\u2013${winStartY + 1}` : "\u2013");
        const hasNote = !!(y.coverage_note);
        const unaccountedGaps = hasNote ? [] : gapMonths;
        const allAccounted = windowMonths.length > 0 && unaccountedGaps.length === 0 && unpricedMonths.length === 0;
        const unaccountedRange = unaccountedGaps.length > 0
          ? `${monthFullName(unaccountedGaps[0])?.split(" ")[0]}\u2013${monthFullName(unaccountedGaps[unaccountedGaps.length - 1])}`
          : "";
        // Delete-year gating: only allow when no substantive terms
        const hasSubstantive = periods.some(isTermSubstantive);

        // Delete-year control (rendered in header, both collapsed and expanded)
        const deleteYearControl = (() => {
          if (hasSubstantive) {
            return (
              <span className="relative group/dyr shrink-0" onClick={(e) => e.stopPropagation()}>
                <Trash size={13} weight="bold" className="text-[#C2C9D1] cursor-not-allowed" data-testid={`coa-remove-year-disabled-${i}-${yi}`} />
                <span className="pointer-events-none absolute right-0 top-full z-50 mt-1 w-44 rounded-md border border-[#C2C9D1] bg-white px-2 py-1 text-[10px] text-[#6b7280] shadow-lg opacity-0 group-hover/dyr:opacity-100">Delete the terms in this year first.</span>
              </span>
            );
          }
          return (
            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDelYr([i, yi]); }} className="shrink-0 text-[#F01919]" data-testid={`coa-remove-year-${i}-${yi}`} aria-label="Delete this academic year"><Trash size={13} weight="bold" /></button>
          );
        })();

        return (
          <div key={yi} className="rounded-md border border-[#C2C9D1] bg-white" data-testid={`coa-year-${i}-${yi}`}>
            {/* Header — always visible, clickable to toggle */}
            <div className="flex w-full items-center gap-2 px-2 py-1.5">
              <button type="button" onClick={() => toggleYear(yi)} className="flex flex-1 items-center gap-2" data-testid={`coa-year-toggle-${i}-${yi}`}>
                <CaretDown size={14} weight="bold" className={`shrink-0 text-[#6b7280] transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-[#6b7280]">
                  Academic year
                  <span className="rounded border border-[#C2C9D1] bg-[#F4F7FB] px-2 py-0.5 text-sm font-semibold normal-case text-[#0A0A0A]" data-testid={`coa-year-label-${i}-${yi}`}>
                    {spanLabel}
                  </span>
                </span>
                {!isOpen && (
                  <span className={`ml-1 text-[10px] font-normal normal-case ${allAccounted ? "text-[#374151]" : (unaccountedGaps.length > 0 || unpricedMonths.length > 0) ? "text-[#D97706]" : "text-[#9CA3AF]"}`} data-testid={`coa-year-summary-${i}-${yi}`}>
                    {allAccounted
                      ? "All months accounted for"
                      : unaccountedGaps.length > 0
                        ? `${unaccountedGaps.length} month${unaccountedGaps.length !== 1 ? "s" : ""} unaccounted (${unaccountedRange})`
                        : unpricedMonths.length > 0
                          ? `${unpricedMonths.length} month${unpricedMonths.length !== 1 ? "s" : ""} still need an amount`
                          : ""}
                  </span>
                )}
              </button>
              {deleteYearControl}
            </div>
            {/* Slim coverage strip — shown when collapsed */}
            {!isOpen && stripMonths.length > 0 && (
              <div className="px-2 pb-1.5" data-testid={`coa-strip-mini-${i}-${yi}`}>
                <div className="flex gap-px overflow-hidden rounded">
                  {stripMonths.map((m) => {
                    const pis = coverMap[m] || [];
                    const isOverlap = pis.length > 1;
                    const bg = isOverlap ? "#F01919" : pis.length === 1 ? (TERM_TINTS[pis[0] % TERM_TINTS.length]) : "#E5E7EB";
                    return <div key={m} className="h-2 min-w-[8px] flex-1 rounded-sm" style={{ background: bg }} title={`${monthFullName(m)}${pis.length ? ` — ${(periods[pis[0]]?.term_label || guessTermLabel(periods[pis[0]]?.from_month, periods[pis[0]]?.to_month))}` : " — not covered"}`} />;
                  })}
                </div>
              </div>
            )}
            {/* Expanded body */}
            {isOpen && (
              <div className="px-2 pb-2">
                <div className="mb-1.5 flex flex-wrap items-center justify-end gap-1.5">
                  {windowFull ? (
                    <span className="text-[10px] text-[#9CA3AF]" data-testid={`coa-year-full-${i}-${yi}`}>This academic year is fully covered. Use &ldquo;Add new academic year&rdquo; for {nextYearLabel}.</span>
                  ) : nextInWindow ? (
                    <Btn variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); addPd(i, yi); }} data-testid={`coa-add-period-${i}-${yi}`} title={`Starts ${monthFullName(nextStart)}`}>
                      <Plus size={12} weight="bold" /> <span className="hidden sm:inline">Add term (starts {monthFullName(nextStart)})</span><span className="sm:hidden">Add term</span>
                    </Btn>
                  ) : (
                    <Btn variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); addPd(i, yi); }} data-testid={`coa-add-period-${i}-${yi}`}>
                      <Plus size={12} weight="bold" /> Add term
                    </Btn>
                  )}
                </div>
                {/* Coverage strip */}
                {stripMonths.length > 0 && (
                  <div className="mb-2" data-testid={`coa-strip-${i}-${yi}`}>
                    <div className="flex gap-px overflow-x-auto rounded">
                      {stripMonths.map((m) => {
                        const pis = coverMap[m] || [];
                        const isOverlap = pis.length > 1;
                        const bg = isOverlap ? "#F01919" : pis.length === 1 ? (TERM_TINTS[pis[0] % TERM_TINTS.length]) : "#E5E7EB";
                        return <div key={m} className="flex min-w-[20px] flex-1 flex-col items-center" title={`${monthFullName(m)}${pis.length ? ` — ${(periods[pis[0]]?.term_label || guessTermLabel(periods[pis[0]]?.from_month, periods[pis[0]]?.to_month))}` : " — not covered"}`}>
                          <div className="h-3 w-full rounded-sm" style={{ background: bg }} />
                          <span className="mt-0.5 text-[8px] text-[#6b7280]">{MONTH_INITIALS[parseInt(m.split("-")[1]) - 1]}</span>
                        </div>;
                      })}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {periods.map((p, pi) => (
                        <span key={pi} className="flex items-center gap-1 text-[9px] text-[#6b7280]">
                          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: TERM_TINTS[pi % TERM_TINTS.length] }} />
                          {p.term_label || guessTermLabel(p.from_month, p.to_month)} · {monthFullName(p.from_month)?.split(" ")[0]} – {monthFullName(p.to_month)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Gap note */}
                {gapMonths.length > 0 && (
                  <div className="mb-2 space-y-1.5" data-testid={`coa-gap-note-${i}-${yi}`}>
                    <div className="rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-1.5 text-[11px] text-[#92400E]">
                      {gapNames.join(", ")} isn't covered by any term, so room &amp; board in {gapMonths.length === 1 ? "that month" : "those months"} won't count toward the cap. That's expected if your beneficiary wasn't enrolled then (e.g., started mid-year, summer break, or a co-op term).
                      {!y.coverage_note && !y._showNoteInput && (
                        <button type="button" onClick={() => setYr(i, yi, "_showNoteInput", true)} className="ml-1 font-semibold text-[#92400E] underline" data-testid={`coa-gap-add-note-${i}-${yi}`}>Add a note</button>
                      )}
                    </div>
                    {(y.coverage_note || y._showNoteInput) && (
                      <div className="rounded-md border border-[#C2C9D1] bg-white px-3 py-1.5">
                        <label className="block text-[10px] font-bold uppercase text-[#6b7280]">Owner note on enrollment coverage</label>
                        <input value={y.coverage_note || ""} onChange={(e) => setYr(i, yi, "coverage_note", e.target.value)}
                          placeholder="e.g., Started in January — first enrolled for the winter term"
                          data-testid={`coa-coverage-note-${i}-${yi}`}
                          className="mt-1 h-8 w-full rounded-md border border-[#C2C9D1] px-2 text-xs" />
                      </div>
                    )}
                  </div>
                )}
                {gapMonths.length === 0 && y.coverage_note && (
                  <div className="mb-2 rounded-md border border-[#C2C9D1] bg-white px-3 py-1.5">
                    <label className="block text-[10px] font-bold uppercase text-[#6b7280]">Owner note on enrollment coverage</label>
                    <input value={y.coverage_note || ""} onChange={(e) => setYr(i, yi, "coverage_note", e.target.value)}
                      data-testid={`coa-coverage-note-${i}-${yi}`}
                      className="mt-1 h-8 w-full rounded-md border border-[#C2C9D1] px-2 text-xs" />
                  </div>
                )}
                {unpricedMonths.length > 0 && (
                  <div className="mb-2 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-1.5 text-[11px] text-[#92400E]" data-testid={`coa-unpriced-note-${i}-${yi}`}>
                    {unpricedNames.join(", ")} {unpricedMonths.length === 1 ? "is covered by a term that has" : "are covered by terms that have"} no dollar amount yet, so room &amp; board in {unpricedMonths.length === 1 ? "that month" : "those months"} counts as nothing toward the cap. Enter the published COA figure on {unpricedMonths.length === 1 ? "that term" : "those terms"} below.
                  </div>
                )}
                <p className="mb-1 text-[10px] text-[#9CA3AF]">New term needed only when living situation, COA rate, or half-time status changes.</p>
                <div className="space-y-1.5">
                  {periods.map((p, pi) => (
                    <PeriodRow key={p.id || pi} p={p} testbase={`coa-${i}-${yi}-${pi}`} termColor={TERM_TINTS[pi % TERM_TINTS.length]}
                      onChange={(k, v) => setPd(i, yi, pi, k, v)} onRemove={() => removePd(i, yi, pi)} canRemove={periods.length > 1}
                      schoolCurrency={s.currency || "USD"} studentId={studentId}
                      isOpen={termOpenMap[yi] === pi} onToggle={() => toggleTerm(yi, pi)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A term crossing a calendar year is split in two on save; say where, in the
// terms table's month words (ZEN-788). Nothing for a term within one year.
export function yearSplitHint(p) {
  const fy = parseInt((p.from_month || "").split("-")[0]);
  const ty = parseInt((p.to_month || "").split("-")[0]);
  if (!fy || !ty || fy === ty) return "";
  return `Year boundary (${fy}→${ty}): will auto-split on save to ${termMonthsShort(p.from_month, `${fy}-12`)} and ${termMonthsShort(`${fy + 1}-01`, p.to_month)}.`;
}

// Which term's paperwork this is, for anyone who cannot see the row above it.
export function coaDocRecordLabel(p) {
  // ZEN-788: the months read as the terms table writes them ("Sep–Dec 2026").
  const span = termMonthsShort(p.from_month, p.to_month);
  return `${p.term_label || "term"}${span ? ` (${span})` : ""}`;
}

function PeriodRow({ p, onChange, onRemove, canRemove, testbase, schoolCurrency = "USD", termColor = "#C8DBEF", isOpen: controlledOpen, onToggle, studentId }) {
  const docPanel = useAttachmentPanel();
  const months = monthsBetween(p.from_month, p.to_month);
  const cur = p.coa_currency || schoolCurrency || "USD";
  const fx = cur === "USD" ? 1.0 : (parseFloat(p.coa_fx_rate) || 0);
  const [fetchingFx, setFetchingFx] = useState(false);
  const [rateFetchFailed, setRateFetchFailed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmDocRemove, setConfirmDocRemove] = useState(null);
  const uploadsInFlight = React.useContext(CoaUploadsInFlight);
  const [housingManual, setHousingManual] = useState(false);
  const open = controlledOpen != null ? controlledOpen : true;
  const toggleOpen = onToggle || (() => {});
  const [fxOpen, setFxOpen] = useState(false);
  const [pub970Open, setPub970Open] = useState(false);

  const termDeleteDialog = canRemove ? (
    <DeleteDialog
      open={confirmDel}
      onClose={() => setConfirmDel(false)}
      onConfirm={() => { onRemove(); setConfirmDel(false); }}
      noun="term"
      testid={`${testbase}-remove`}
      body={isTermSubstantive(p) ? (
        <>
          <p className="text-sm text-[#374151]">This term has COA data that will be <b>permanently</b> removed.</p>
          <p className="text-sm font-bold text-[#374151]">This can&apos;t be undone.</p>
        </>
      ) : undefined}
    />
  ) : null;

  const ON_CAMPUS_SITUATIONS = ["on_campus"];
  const handleSituationChange = (val) => {
    onChange("situation", val);
    if (!housingManual) onChange("housing_type", ON_CAMPUS_SITUATIONS.includes(val) ? "school_housing" : "off_campus");
  };
  const handleHousingTypeChange = (val) => { setHousingManual(true); onChange("housing_type", val); };
  const termLabel = p.term_label || guessTermLabel(p.from_month, p.to_month);
  const dateRange = p.from_month && p.to_month ? `${monthFullName(p.from_month)?.split(" ")[0]}\u2013${monthFullName(p.to_month)}` : "";
  const handleDocPick = async (files) => {
    const fileList = Array.isArray(files) ? files : Array.from(files || []);
    if (!fileList.length) return;
    setUploading(true);
    if (uploadsInFlight) uploadsInFlight.current += 1;
    const uploaded = [];
    try {
      for (const file of fileList) {
        try {
          const fd = new FormData(); fd.append("file", file);
          const { data } = await api.post("/students/coa-doc-upload", fd);
          // upload_id marks a not-yet-saved doc: previews go through the upload
          // registry until a save attaches the doc to the student under its id.
          uploaded.push({ id: data.id, upload_id: data.id, path: data.path, name: data.filename });
        } catch (e) { toast.error("Upload failed: " + apiErr(e)); }
      }
      // Append to whatever has landed by now: a second upload running alongside
      // this one must not be erased, and a batch that wholly failed writes nothing.
      if (uploaded.length) onChange("docs", (prev) => [...(prev || []), ...uploaded]);
    } finally {
      if (uploadsInFlight) uploadsInFlight.current -= 1;
    }
    setUploading(false);
  };
  const periodStartIsFuture = p.from_month && `${p.from_month}-01` > localISO();

  useEffect(() => {
    if (cur === "USD" || !p.from_month) return;
    if (periodStartIsFuture) { if (p.coa_fx_rate && p.coa_fx_date && !p.coa_fx_date.startsWith(p.from_month)) { onChange("coa_fx_rate", null); onChange("coa_fx_date", null); } return; }
    const expectedDate = `${p.from_month}-01`;
    if (p.coa_fx_date && p.coa_fx_date.startsWith(p.from_month)) return;
    let active = true; setFetchingFx(true); setRateFetchFailed(false);
    api.post("/fx/preview", { date: expectedDate, base: cur, target: "USD" })
      .then((res) => { if (!active) return; onChange("coa_fx_rate", res.data.rate); onChange("coa_fx_date", res.data.rate_date || expectedDate); setRateFetchFailed(false); })
      .catch(() => { if (!active) return; setRateFetchFailed(true); setFxOpen(true); })
      .finally(() => { if (active) setFetchingFx(false); });
    return () => { active = false; };
  }, [p.from_month, cur]); // eslint-disable-line

  // Non-USD period with no usable rate — must never silently sit at $0
  const coaRateRequired = cur !== "USD" && !fx && !fetchingFx && !periodStartIsFuture && !!p.from_month;

  // Computed USD totals for summary
  const fmtUsd = (n) => money(n, "USD");
  const computeCoaUsd = () => {
    const basis = p.basis || "monthly";
    let totalNative = 0;
    if ((p.mode || "separate") === "combined") {
      const v = parseFloat(p.combined) || 0;
      totalNative = basis === "monthly" ? v * months : v;
    } else {
      const food = parseFloat(p.food) || 0;
      const housing = parseFloat(p.housing) || 0;
      totalNative = basis === "monthly" ? (food + housing) * months : food + housing;
    }
    return totalNative * (cur === "USD" ? 1.0 : fx);
  };
  const coaUsd = computeCoaUsd();
  const computeCoaNative = () => {
    const basis = p.basis || "monthly";
    if ((p.mode || "separate") === "combined") {
      const v = parseFloat(p.combined) || 0;
      return basis === "monthly" ? v * months : v;
    }
    const food = parseFloat(p.food) || 0;
    const housing = parseFloat(p.housing) || 0;
    return basis === "monthly" ? (food + housing) * months : food + housing;
  };
  const coaNative = computeCoaNative();
  const invoiceNative = parseFloat(p.school_invoiced_amount) || 0;
  const invoiceUsd = invoiceNative * (cur === "USD" ? 1.0 : fx);
  const capUsd = (p.housing_type === "school_housing" && invoiceNative > 0) ? Math.max(coaUsd, invoiceUsd) : coaUsd;
  const isSchoolHousing = p.housing_type === "school_housing";

  // --- COLLAPSED SUMMARY ROW ---
  if (!open) {
    return (
      <div className="rounded-md border border-[#C2C9D1] bg-[#F8FAFC]" data-testid={`${testbase}-period`}>
        {termDeleteDialog}
        <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer" data-testid={`${testbase}-toggle`} onClick={toggleOpen}>
          <CaretDown size={12} weight="bold" className="-rotate-90 shrink-0 text-[#6b7280]" />
          <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: termColor }} />
          <span className="text-sm font-semibold text-[#0A0A0A] truncate">{termLabel}</span>
          <span className="hidden sm:inline text-[10px] text-[#6b7280] truncate">{dateRange}{months ? ` · ${months}mo` : ""}</span>
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${isSchoolHousing ? "bg-[#034AA2]" : "bg-[#6b7280]"}`}>{isSchoolHousing ? "School" : "Off campus"}</span>
          {(p.half_time !== false) && <span className="text-[9px] font-bold text-[#077A39]">½+</span>}
          <span className="text-[10px] text-[#6b7280] tabular-nums truncate">COA {fmtUsd(coaUsd)}{isSchoolHousing && invoiceNative > 0 ? ` · inv ${fmtUsd(invoiceUsd)}` : ""}</span>
          <span className="flex-1" />
          {canRemove && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDel(true); }} className="shrink-0 text-[#F01919]" data-testid={`${testbase}-remove`} aria-label="remove term"><Trash size={12} weight="bold" /></button>
          )}
        </div>
      </div>
    );
  }

  // --- EXPANDED CARD ---
  return (
    <div className="rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2" data-testid={`${testbase}-period`}>
      {termDeleteDialog}
      {/* Collapse header */}
      <div className="mb-1.5 flex items-center gap-2 cursor-pointer" data-testid={`${testbase}-toggle`} onClick={toggleOpen}>
        <CaretDown size={12} weight="bold" className="shrink-0 text-[#6b7280]" />
        <span className="h-3.5 w-3.5 shrink-0 rounded-sm" style={{ background: termColor }} />
        <input value={p.term_label || ""} onChange={(e) => onChange("term_label", e.target.value)}
          placeholder={guessTermLabel(p.from_month, p.to_month)} data-testid={`${testbase}-term-label`}
          onClick={(e) => e.stopPropagation()}
          className="h-6 w-28 min-w-0 rounded border border-transparent bg-transparent px-1 text-sm font-semibold text-[#0A0A0A] placeholder:text-[#9CA3AF] hover:border-[#C2C9D1] focus:border-[#034AA2] focus:outline-none" />
        <span className="flex-1" />
        {canRemove && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDel(true); }} className="shrink-0 text-[#F01919]" data-testid={`${testbase}-remove`} aria-label="remove term"><Trash size={12} weight="bold" /></button>
        )}
      </div>

      {/* Row 1: From | To | Living situation (3-col on sm+, stack on mobile) */}
      <div className="mb-1.5 grid grid-cols-1 gap-1.5 sm:grid-cols-3">
        <label className="block"><span className="mb-0.5 block text-[9px] font-bold uppercase text-[#6b7280]">From</span>
          <input type="month" value={p.from_month} onChange={(e) => onChange("from_month", e.target.value)} data-testid={`${testbase}-from`} className="h-8 w-full rounded-md border border-[#C2C9D1] px-1.5 text-xs" /></label>
        <label className="block"><span className="mb-0.5 block text-[9px] font-bold uppercase text-[#6b7280]">To</span>
          <input type="month" value={p.to_month} onChange={(e) => onChange("to_month", e.target.value)} data-testid={`${testbase}-to`} className="h-8 w-full rounded-md border border-[#C2C9D1] px-1.5 text-xs" /></label>
        <div>
          <span className="mb-0.5 block text-[9px] font-bold uppercase text-[#6b7280]">Situation</span>
          <select value={p.situation} onChange={(e) => handleSituationChange(e.target.value)} data-testid={`${testbase}-situation`} className="h-8 w-full rounded-md border border-[#C2C9D1] bg-white px-1.5 text-xs">
            {SITUATIONS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
          {p.situation === "other" && (
            <input value={p.situation_custom} onChange={(e) => onChange("situation_custom", e.target.value)} data-testid={`${testbase}-situation-custom`} placeholder="School's category name" className="mt-1 h-7 w-full rounded-md border border-[#C2C9D1] px-1.5 text-xs" />
          )}
        </div>
      </div>

      {/* Row 2: Half-time | Housing-type (with ⓘ tooltip) | Currency summary */}
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-[#374151]" title="Room & board is only 529-qualified when enrolled at least half-time">
          <Switch checked={p.half_time !== false} onCheckedChange={(v) => onChange("half_time", v)} data-testid={`${testbase}-halftime`} />
          <span className="text-[10px]">½-time+</span>
        </label>
        <div className="flex items-center gap-1">
          <select value={p.housing_type || "off_campus"} onChange={(e) => handleHousingTypeChange(e.target.value)} data-testid={`${testbase}-housing-type`} className="h-7 rounded-md border border-[#C2C9D1] bg-white px-1.5 text-[10px]">
            <option value="off_campus">Off campus</option>
            <option value="school_housing">School housing</option>
          </select>
          {isSchoolHousing && (
            <span className="relative" data-testid={`${testbase}-pub970-tip-wrap`}>
              <button type="button" onClick={() => setPub970Open(!pub970Open)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPub970Open(!pub970Open); } }}
                className="flex h-5 w-5 items-center justify-center rounded-full text-[#034AA2] hover:bg-[#034AA2]/10 focus:outline-none focus:ring-2 focus:ring-[#034AA2]"
                data-testid={`${testbase}-pub970-tip-btn`} aria-label="IRS Pub 970 info" aria-expanded={pub970Open}>
                <Info size={13} weight="bold" />
              </button>
              {pub970Open && (
                <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-[#C2C9D1] bg-white px-3 py-2 text-[10px] text-[#374151] shadow-lg" data-testid={`${testbase}-pub970-tip`}>
                  IRS Pub 970: for school-owned housing, the qualifying amount is the <b>greater of</b> the Cost of Attendance (COA) allowance or the actual amount invoiced by the school. Spending beyond <b>both</b> the COA allowance and the invoiced amount (e.g., supplemental groceries past that headroom) is not qualified.
                  <button type="button" onClick={() => setPub970Open(false)} className="ml-1 font-semibold text-[#034AA2] underline">Close</button>
                </div>
              )}
            </span>
          )}
        </div>
        {/* Compact currency summary */}
        {cur !== "USD" && (
          <div className="flex items-center gap-1 text-[10px] text-[#6b7280]">
            <span className="font-semibold text-[#034AA2]">{cur}</span>
            <span>·</span>
            {fetchingFx
              ? <span className="italic text-[#F68F51]">fetching rate…</span>
              : periodStartIsFuture
                ? <span className="italic text-[#F68F51]">rate pending</span>
                : fx
                  ? <span>1 {cur} = {fx.toFixed(4)} USD <span className="text-[#9CA3AF]">(locked {p.coa_fx_date || ""})</span></span>
                  : p.from_month
                    ? <span className="font-semibold text-[#F01919]">no rate — enter manually</span>
                    : <span className="italic text-[#F68F51]">set FROM month</span>}
            <button type="button" onClick={() => setFxOpen(!fxOpen)} data-testid={`${testbase}-fx-toggle`}
              className="ml-0.5 rounded p-0.5 text-[#034AA2] hover:bg-[#034AA2]/10" aria-label="FX details">
              <ArrowsClockwise size={11} weight="bold" />
            </button>
          </div>
        )}
        <CurrencySelect value={cur} onChange={(v) => { onChange("coa_currency", v); onChange("coa_fx_rate", v === "USD" ? 1.0 : null); onChange("coa_fx_date", null); setRateFetchFailed(false); }} testid={`${testbase}-currency`} />
      </div>

      {/* COA rate required warning — non-USD period with no rate */}
      {coaRateRequired && (
        <div className="mb-1.5 rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-2.5 py-1.5" data-testid={`${testbase}-rate-required`}>
          <p className="text-xs font-medium text-[#92400E]">
            <Warning size={12} weight="bold" className="mr-1 inline" />
            {noAutomaticRateFor(cur)}
          </p>
        </div>
      )}

      {/* FX details panel — auto-opens when rate is required */}
      {cur !== "USD" && (fxOpen || coaRateRequired) && (
        <div className="mb-1.5 rounded-md border border-[#C2C9D1] bg-white p-2" data-testid={`${testbase}-fx`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold">Currency conversion <span className="font-normal text-[#6b7280]">{periodStartIsFuture ? "(will lock on period start)" : "(locked at period start)"}</span></span>
            <label className="flex items-center gap-1 text-[10px] font-medium">
              <input type="checkbox" checked={!!p.coa_fx_override} onChange={(e) => onChange("coa_fx_override", e.target.checked)} data-testid={`${testbase}-fx-override`} />
              Override
            </label>
          </div>
          <div className="mt-1 text-xs">
            {p.coa_fx_override ? (
              <label className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold text-[#6b7280]">Rate</span>
                <input type="number" step="0.00001" value={p.coa_fx_rate || ""} onChange={(e) => onChange("coa_fx_rate", e.target.value)}
                  data-testid={`${testbase}-fx-rate-input`} placeholder="e.g., 0.72500" className="h-7 w-28 rounded border border-[#C2C9D1] px-1.5 text-xs" />
              </label>
            ) : (
              <span className="text-[#6b7280]">{fx ? `1 ${cur} = ${Number(fx).toFixed(5)} USD · ECB · ${p.coa_fx_date || ""}` : coaRateRequired ? noAutomaticRateFor(cur) : "Set FROM month to auto-fetch rate"}</span>
            )}
          </div>
        </div>
      )}

      {/* Year-boundary split hint */}
      {yearSplitHint(p) && (
        <div className="mb-1.5 rounded-md border border-[#034AA2]/30 bg-[#034AA2]/5 px-2 py-1 text-[10px] text-[#034AA2]" data-testid={`${testbase}-split-hint`}>
          {yearSplitHint(p)}
        </div>
      )}

      {/* Row 3: COA caps + invoice — mode toggle + money grid */}
      <div className="mb-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[9px] font-bold uppercase tracking-wide text-[#6b7280]">Room &amp; board (R&amp;B)</span>
          <InfoTip testid={`${testbase}-rb-tooltip`} size={12} width="w-80">
            Schools publish their official Cost of Attendance (COA) room &amp; board allowance either as separate housing and food amounts or as one combined figure — enter it the way your school publishes it on their financial-aid COA page. Under IRS Pub 970, this allowance is the cap on qualified room &amp; board (for school-owned housing, the cap is the greater of this allowance and what the school actually invoices), and room &amp; board only qualifies while enrolled at least half-time.
          </InfoTip>
          <div className="flex rounded-md border border-[#C2C9D1] p-0.5 text-[9px] font-semibold">
            <button type="button" onClick={() => onChange("mode", "separate")} data-testid={`${testbase}-mode-sep`} className={`rounded px-1.5 py-0.5 ${(p.mode || "separate") === "separate" ? "bg-[#034AA2] text-white" : "text-[#6b7280]"}`}>Separate</button>
            <button type="button" onClick={() => onChange("mode", "combined")} data-testid={`${testbase}-mode-comb`} className={`rounded px-1.5 py-0.5 ${p.mode === "combined" ? "bg-[#034AA2] text-white" : "text-[#6b7280]"}`}>Combined</button>
          </div>
          {!hasActiveCoaAmount(p) && p.half_time !== false && (
            <label className="ml-auto flex items-center gap-1 text-[10px] font-semibold text-[#374151]">
              <input type="checkbox" checked={!!p.enter_later} onChange={(e) => onChange("enter_later", e.target.checked)} data-testid={`${testbase}-enter-later`} className="h-3.5 w-3.5 accent-[#034AA2]" />
              Enter later
            </label>
          )}
        </div>
        {(p.mode || "separate") === "combined" ? (
          <div className={`grid gap-1.5 ${isSchoolHousing ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
            <TwoWayMoneyIn label="" val={p.combined} basis={p.basis} months={months} onVal={(v) => onChange("combined", v)} onBasis={(b) => onChange("basis", b)} test={`${testbase}-combined`} currency={cur} monthlyLabel="Published COA per month" periodLabel={`COA period total${months > 0 ? ` (${months} mo)` : ""}`} />
            {isSchoolHousing && (
              <SchoolInvoicedInput val={p.school_invoiced_amount} onChange={(v) => onChange("school_invoiced_amount", v)} months={months} currency={cur} testid={`${testbase}-school-invoiced`} />
            )}
          </div>
        ) : (
          <div className={`grid gap-1.5 ${isSchoolHousing ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
            <TwoWayMoneyIn label="Published COA — food" val={p.food} basis={p.basis} months={months} onVal={(v) => onChange("food", v)} onBasis={(b) => onChange("basis", b)} test={`${testbase}-food`} currency={cur} monthlyLabel="Per month" periodLabel={`Period total${months > 0 ? ` (${months} mo)` : ""}`} />
            <TwoWayMoneyIn label="Published COA — housing" val={p.housing} basis={p.basis} months={months} onVal={(v) => onChange("housing", v)} onBasis={(b) => onChange("basis", b)} test={`${testbase}-housing`} currency={cur} monthlyLabel="Per month" periodLabel={`Period total${months > 0 ? ` (${months} mo)` : ""}`} />
            {isSchoolHousing && (
              <div>
                <span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">Invoiced</span>
                <SchoolInvoicedInput val={p.school_invoiced_amount} onChange={(v) => onChange("school_invoiced_amount", v)} months={months} currency={cur} testid={`${testbase}-school-invoiced`} />
              </div>
            )}
          </div>
        )}
      </div>

      {p._copied && (
        <div className="mb-1 text-[10px] text-[#6b7280]" data-testid={`${testbase}-copied`}>{copiedTermLine(p._copied.from)}</div>
      )}

      {/* Derivation line */}
      {coaUsd > 0 && (
        <div className="mb-1 text-[10px] font-semibold text-[#077A39]" data-testid={`${testbase}-usd-summary`}>
          Qualified cap: {fmtUsd(capUsd)}{isSchoolHousing && invoiceNative > 0 ? <> (COA {fmtUsd(coaUsd)} vs invoiced {fmtUsd(invoiceUsd)})</> : ""}
          {months > 0 ? <span className="font-normal text-[#6b7280]"> · {months}mo</span> : ""}
        </div>
      )}

      {/* Headroom note — shown when school_housing, invoice > 0, COA > invoice */}
      {isSchoolHousing && invoiceNative > 0 && coaNative > invoiceNative + 0.005 && (() => {
        const headroomNative = coaNative - invoiceNative;
        const headroomUsd = coaUsd - invoiceUsd;
        const fmtNative = (n) => money(n, cur);
        return (
          <div className="mb-1 text-[10px] font-semibold text-[#077A39]" data-testid={`${testbase}-headroom-note`}>
            Grocery headroom: {fmtNative(headroomNative)}{cur !== "USD" ? ` (${fmtUsd(headroomUsd)})` : ""} — the published COA exceeds the school&apos;s invoice, so room &amp; board spending beyond the invoice (e.g., groceries) can still qualify, up to this amount this term.
          </div>
        );
      })()}

      {/* Inverse note — shown when school_housing, invoice > 0, invoice > COA */}
      {isSchoolHousing && invoiceNative > 0 && invoiceNative > coaNative + 0.005 && (
        <div className="mb-1 text-[10px] text-[#6b7280]" data-testid={`${testbase}-inverse-note`}>
          The school&apos;s invoice exceeds the published COA, so the invoice itself is your cap — food spending beyond it doesn&apos;t qualify.
        </div>
      )}

      {/* Contextual hints (invoice split, enter-invoice amber hint) */}
      {isSchoolHousing && !invoiceNative && (p.half_time !== false) && (
        <div className="mb-1 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1 text-[10px] text-[#92400E]" data-testid={`${testbase}-invoice-hint`}>
          Enter the school-invoiced amount to apply the IRS greater-of rule.
        </div>
      )}
      {isSchoolHousing && invoiceNative > 0 && (() => { const ify = parseInt((p.from_month || "").split("-")[0]); const ity = parseInt((p.to_month || "").split("-")[0]); return ify && ity && ify !== ity ? (
        <div className="mb-1 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1 text-[10px] text-[#92400E]" data-testid={`${testbase}-invoice-split-note`}>
          This term spans two tax years — the invoiced amount will be split proportionally.
        </div>
      ) : null; })()}
      {/* Same rule the year's month accounting uses, so the term and the year
          summary can never disagree about whether a figure is still owed. */}
      {termOwesAmount(p) && (
        <div className="mb-1 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1 text-[10px] text-[#92400E]" data-testid={`${testbase}-docs-no-amount-hint`}>
          {(p.docs?.length > 0)
            ? <>You&apos;ve uploaded a document for this term but haven&apos;t entered a dollar amount yet</>
            : <>No dollar amount entered for this term yet</>} — it won&apos;t count toward the room &amp; board cap until you enter the COA figure above.
        </div>
      )}

      {/* Notes + Supporting document — final row */}
      <div className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <NotesField className="sm:col-span-2" value={p.note} onChange={(v) => onChange("note", v)} testid={`${testbase}-note`} />
        <div>
          <span className="mb-0.5 block text-[9px] font-bold uppercase text-[#6b7280]">Documents</span>
          {/* Existing docs list */}
          {/* THE DOCUMENT OPENS HERE, NOT IN A NEW TAB. What these prove is the
              cost of attendance for this term — the very figures sitting a few
              inches above them. A new tab takes those figures away at exactly
              the moment somebody is checking the school's paperwork against
              them, so the document opens beneath the list instead. (ZEN-351) */}
          {(p.docs?.length > 0) && (
            <div className="mb-1 space-y-0.5" data-testid={`${testbase}-doc-list`}>
              {p.docs.map((d, di) => {
                const k = `${testbase}:${di}`;
                const att = {
                  key: k,
                  label: d.name || "Document",
                  fileUrl: d.upload_id ? coaUploadUrl(d.upload_id) : (studentId && d.id ? coaDocUrl(studentId, d.id) : coaDocLegacyUrl(d.path)),
                };
                return (
                  <div key={di}>
                    <div className="flex items-center gap-1.5">
                      <AttachmentButton
                        ref={docPanel.buttonRef(k)}
                        variant="chip"
                        attachments={[att]}
                        recordLabel={coaDocRecordLabel(p)}
                        expanded={docPanel.isOpen(k)}
                        panelId={docPanel.panelId(k)}
                        onToggle={() => docPanel.toggle(k)}
                        testid={`${testbase}-doc-link-${di}`}
                      />
                      <button type="button" onClick={() => setConfirmDocRemove(di)} className="ml-auto shrink-0 rounded p-0.5 text-[#F01919] hover:bg-[#F01919]/10" data-testid={`${testbase}-doc-remove-${di}`} title="Remove document"><Trash size={12} weight="bold" /></button>
                    </div>
                    {docPanel.isOpen(k) && (
                      <AttachmentPanel
                        id={docPanel.panelId(k)}
                        attachments={[att]}
                        recordLabel={coaDocRecordLabel(p)}
                        onClose={() => docPanel.close(k)}
                        testid={`${testbase}-doc-panel-${di}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DeleteDialog
            open={confirmDocRemove !== null}
            onClose={() => setConfirmDocRemove(null)}
            onConfirm={() => { const idx = confirmDocRemove; onChange("docs", (prev) => (prev || []).filter((_, i) => i !== idx)); setConfirmDocRemove(null); }}
            noun="document"
            testid={`${testbase}-doc-delete`}
          />
          <label className={`flex cursor-pointer items-center justify-center gap-1 rounded-md border-2 border-dashed px-2 py-1.5 text-[10px] transition-colors ${dragOver ? "border-[#034AA2] bg-[#034AA2]/5" : "border-[#C2C9D1] bg-white hover:border-[#034AA2]"}`}
            data-testid={`${testbase}-doc-label`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)} onDrop={(e) => { e.preventDefault(); setDragOver(false); handleDocPick(Array.from(e.dataTransfer.files || [])); }}>
            <Paperclip size={12} weight="bold" className={dragOver ? "text-[#034AA2]" : "text-[#6b7280]"} />
            <span className="text-[#6b7280]">{uploading ? "Uploading…" : dragOver ? "Drop" : "Attach files"}</span>
            <input type="file" accept={DOCUMENT_ACCEPT} multiple className="hidden" onChange={(e) => { handleDocPick(e.target.files); e.target.value = ""; }} data-testid={`${testbase}-doc-input`} />
          </label>
        </div>
      </div>
    </div>
  );
}


function LoginDialog({ student, onClose, onSaved }) {
  const [email, setEmail] = useState(student.login_email || ""); const [pw, setPw] = useState("");
  const existing = (student.login_email || "").trim().toLowerCase();
  // Normalized on BOTH sides, because the server normalizes before it compares.
  // Without this, a stray space or a capital letter would announce "the address
  // is changing" while the server correctly performs a same-address reset.
  const typed = (email || "").trim().toLowerCase();
  const hasLogin = !!existing;
  const addressIsNew = !hasLogin || typed !== existing;
  const save = async () => {
    try {
      // Sent explicitly, and bound to the same decision the server makes. Left
      // to its default this field is true, so every password reset used to mail
      // the beneficiary a setup invitation carrying their private receipt address.
      const res = await api.post(`/students/${student.id}/login`, { email, password: pw, send_invite: addressIsNew });
      // Show what the SERVER says happened, not what we assumed would happen.
      // It can come back saying the password changed but the beneficiary was
      // archived, deleted or repointed at the same moment — in which case the
      // login does not work, and "Login updated" would be a false reassurance
      // about who can reach a family's money.
      // Read the FLAG, not the prose. An earlier version matched on the message
      // text and a rewording on the server silently broke the match, so every
      // partial success displayed as an unqualified one.
      const d = res?.data || {};
      if (d.ok === false) toast.warning(d.message || "Saved, but check the result before relying on it.", { duration: 12000 });
      else toast.success(hasLogin ? "Login updated" : "Login created");
      onSaved(); onClose();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Beneficiary login for {student.full_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6b7280]">Scoped, read-only access (plus receipt upload). They only ever see their own data.</p>
        <label className="block"><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Email</span><input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="stu-login-email" className="h-10 w-full rounded-md border border-[#C2C9D1] px-3 text-sm" /></label>
        <label className="block"><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Password</span><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="stu-login-pw" className="h-10 w-full rounded-md border border-[#C2C9D1] px-3 text-sm" /><span className="mt-1 block text-xs text-[#6b7280]" data-testid="stu-login-pw-hint">At least 8 characters</span></label>
        <p className="text-xs font-semibold text-[#374151]" data-testid="stu-login-effect">
          {!hasLogin
            ? "They will get an email invitation at this address."
            : addressIsNew
              ? `This changes their sign-in address to ${typed || "the new address"}. ${existing} will stop working immediately.`
              : "This sets a new password. They will be signed out on every device."}
        </p>
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} data-testid="stu-login-save">Save login</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



export function ArchiveDialog({ student, onClose, onConfirm }) {
  const qc = useQueryClient();
  // The Overview panel's own counts, asked about this one beneficiary, so the
  // lines and numbers here are the ones that panel shows. Asked once, afresh,
  // each time the box opens, and never re-read behind her back: archiving is
  // offered only on numbers that arrived and are the ones on screen.
  // Each opening has its own request: a slow one from an earlier opening is
  // cancelled on close and never answers this one.
  const [opening] = useState(() => `${Date.now()}-${Math.random()}`);
  const { data: attention, isError, isFetching } = useQuery({
    queryKey: ["attention", student.id, "archive-box", opening],
    queryFn: async ({ signal }) => (await api.get(`/attention?student_id=${encodeURIComponent(student.id)}`, { signal })).data,
    staleTime: Infinity, gcTime: 0, retry: false,
    refetchOnWindowFocus: false, refetchOnReconnect: false,
  });
  const numbersAreIn = !!attention && !isError && !isFetching;
  // Every sentence this box shows, in one place. (ZEN-692)
  // Approved by Maryse: title, paragraph, aboveTheList, login, "Archive anyway"
  // (2026-09-25); archive, cannotCheck, notShownAgain (2026-10-14). The list
  // lines themselves come from the Overview feed.
  const W = {
    title: "Archive this beneficiary?",
    paragraph: `${student.full_name} will be moved to Archived and hidden from your active list. Their unfinished work stops being counted anywhere, though you can still see it by picking their name at the top of the screen. Their records are kept, and you can reactivate them at any time.`,
    aboveTheList: "Archiving stops all of this being counted:",
    login: "Their login is ended immediately.",
    cannotCheck: `We couldn't check what is still unfinished for ${student.full_name}. Close this box and try again.`,
    archive: "Archive",
    archiveAnyway: "Archive anyway",
    // Year alignment does not come back when an archived person is picked
    // (the Tax Forms page offers active beneficiaries only, ZEN-174).
    notShownAgain: "This one is not shown again when you pick their name. Review it on the Tax Forms page before you archive.",
  };
  const outstanding = numbersAreIn ? attention.sources.filter((s) => s.count > 0) : [];
  const confirm = async () => {
    await onConfirm();
    qc.invalidateQueries({ queryKey: ["attention"] });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{W.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#374151]">{W.paragraph}</p>
        {outstanding.length > 0 && (
          <div data-testid="archive-outstanding">
            <p className="text-sm text-[#374151]">{W.aboveTheList}</p>
            <ul className="mt-2 space-y-1.5">
              {outstanding.map((s) => (
                <li key={s.key} className="flex items-center gap-2 rounded-lg border border-[#D97706]/20 bg-[#FCF5EE] px-3 py-2 text-sm text-[#92600A]" data-testid={`archive-outstanding-${s.key}`}>
                  <span className="flex h-6 min-w-[24px] shrink-0 items-center justify-center rounded-full bg-[#D97706] px-1.5 text-[10px] font-bold text-white">{s.count}</span>
                  <span>{s.label}{s.key === "year_alignment" && <span className="mt-0.5 block text-xs italic" data-testid="archive-not-shown-again">{W.notShownAgain}</span>}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {isError && <p className="text-sm text-[#B91C1C]" role="alert" data-testid="archive-cannot-check">{W.cannotCheck}</p>}
        <p className="text-sm font-semibold text-[#374151]">{W.login}</p>
        <DialogFooter>
          <Btn variant="ghost" onClick={onClose} data-testid="archive-cancel">Cancel</Btn>
          <Btn variant="green" onClick={confirm} disabled={!numbersAreIn} data-testid="archive-confirm"><Archive size={14} weight="bold" /> {outstanding.length > 0 ? W.archiveAnyway : W.archive}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteStudentDialog({ student, onClose, onArchive, onDelete }) {
  const [typed, setTyped] = useState("");
  const match = typed.trim() === student.full_name.trim();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Delete this beneficiary?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#374151]">This <b>permanently</b> removes <b>{student.full_name}</b> and all their expenses, 529 distributions, settlements and receipts. If they just graduated or stopped attending, Archive them instead to keep the records.</p>
        <p className="text-sm font-bold text-[#374151]">This can&apos;t be undone. Their login is ended immediately.</p>
        <div className="mt-2 rounded-lg border border-[#F01919] bg-[#F01919]/5 p-3">
          <label className="block text-xs font-semibold text-[#7f1d1d]">To delete, type the beneficiary&apos;s full name: <span className="font-bold">{student.full_name}</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input" className="mt-1 h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" /></label>
        </div>
        <DialogFooter className="mt-2 flex-wrap gap-2">
          <Btn variant="green" onClick={onArchive} data-testid="delete-archive-instead" className="w-full sm:w-auto"><Archive size={14} weight="bold" /> Archive instead</Btn>
          <div className="flex w-full gap-2 sm:w-auto">
            <button onClick={onClose} data-testid="delete-cancel"
              className="flex-1 rounded-md border border-[#C2C9D1] px-4 py-2 text-sm font-bold text-[#374151] hover:bg-[#F4F7FB] sm:flex-none">Cancel</button>
            <button disabled={!match} onClick={onDelete} data-testid="delete-confirm-btn"
              className={`flex-1 rounded-md px-4 py-2 text-sm font-bold sm:flex-none ${match ? "bg-[#F01919] text-white hover:bg-[#c01010]" : "cursor-not-allowed bg-[#E9E9EB] text-[#9aa1a9]"}`}>
              Delete
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

